<template>
    <v-container fluid class="ma-0 pa-0">
        <v-progress-linear
            v-if="loading"
            class="mb-8"
            color="primary"
            indeterminate
        ></v-progress-linear>
        <validation-observer ref="form" v-slot="{ invalid }">
            <v-form @submit.prevent="submit">
                <v-row v-if="approvedDiary">
                    <v-col cols="12">
                        <v-alert
                            text
                            outlined
                            type="success"
                        >
                            Esta diária está <strong>aprovada!</strong>
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row v-if="method == 'update' && fields.is_rejected">
                    <v-col cols="12">
                        <v-alert
                            text
                            outlined
                            type="error"
                            prominent
                            class="rounded-xl rejected-alert"
                        >
                            <div class="d-flex align-start justify-space-between flex-wrap">
                                <div class="d-flex align-start">
                                    <v-icon color="error" class="mr-3">
                                        mdi-alert-circle-outline
                                    </v-icon>

                                    <div>
                                        <div>
                                            <div class="font-weight-bold">
                                                Esta diária está rejeitada.
                                            </div>

                                            <div class="mt-2 rejection-comment">
                                                <strong>Motivo:</strong>
                                                <span>
                                                    {{ fields.latest_rejection_reason || latestRejection?.comment || 'Sem comentário disponível.' }}
                                                </span>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row class="align-center mb-2">
                    <v-col cols="12" md="auto" v-if="method == 'update'" class="d-flex align-center flex-wrap">
                        <v-btn class="primary white--text mr-2 mb-2" rounded small @click="historico.show = true">
                            <v-icon left small>mdi-eye</v-icon>
                            Histórico
                        </v-btn>

                        <RejectDiary
                            v-if="!fields.is_rejected"
                            class="mb-2"
                            :diary-id="fields.id"
                            :disabled="fields.locked || approvedDiary"
                            @rejected="onDiaryRejected"
                        />
                    </v-col>

                    <v-spacer />

                    <v-col cols="12" md="auto" v-if="precisaSincronizarPrecos">
                        <v-btn
                            @click="sincronizarPrecos"
                            class="success mb-2"
                            rounded
                            small
                        >
                            <v-icon left small>mdi-sync</v-icon>
                            Sinc
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="2">
                        <v-text-field 
                            v-if="method=='create'"
                            ref="dateInput"
                            v-model="fields.Data" 
                            type="date" 
                            id="birthday" 
                            label="Insira data"
                            dense outlined
                            :max="maxDate"
                            :rules=[validateDate]
                            @click="openNativeDatePicker"
                            @focus="openNativeDatePicker"
                            @mousedown.prevent="openNativeDatePicker"
                        />

                        <v-text-field
                            v-else
                            dense outlined
                            v-model="fields.Data"
                            disabled
                            label="Data"
                        />
                    </v-col>
                    <v-col cols="12" md="4" v-if="fields.Data != null">
                        <validation-provider v-slot="{ errors }" vid="Obras" name="Obras" rules="required">
                            <v-autocomplete
                                :disabled="method == 'update'"
                                v-model="fields.obra_id"
                                :items="obras"
                                outlined dense
                                :item-text="item => item.Codigo + ' - ' + item.Descricao"
                                item-value="Id"
                                :error-messages="errors"
                                label="Obras *"
                                @change="changeWork"
                            />
                        </validation-provider>
                    </v-col>
                </v-row>

                <v-row v-if="fields.Data != null && fields.obra_id != null">
                    <v-col cols="12" md="6" v-if="ordensTrabalhoDaObra.length > 0">
                        <v-autocomplete
                            v-model="fields.id_ordem_trabalho"
                            :items="ordensTrabalhoDaObra"
                            outlined
                            dense
                            clearable
                            :loading="loadingOrdensTrabalho"
                            :item-text="getOrdemTrabalhoText"
                            item-value="id"
                            label="Ordem de Trabalho"
                            no-data-text="Não existem ordens de trabalho disponíveis para esta obra"
                        >
                            <template v-slot:selection="{ item }">
                                <span>{{ getOrdemTrabalhoText(item) }}</span>
                            </template>

                            <template v-slot:item="{ item }">
                                <v-list-item-content>
                                    <v-list-item-title>
                                        {{ getOrdemTrabalhoText(item) }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </template>
                        </v-autocomplete>
                    </v-col>
                </v-row>
                <v-row v-if="showContent" class="ma-0 pa-0">
                    <v-col cols="12" class="pa-0">
                        <v-card class="rounded-xl" flat outlined>
                            <v-card-title class="primary white--text d-flex align-center justify-space-between">
                                <span>Carregar:</span>

                                <!-- Bloco da meteorologia à direita -->
                                <div v-if="weatherSummary" class="d-flex align-center">
                                    <v-icon large class="mr-2">
                                    {{ weatherSummary.icon }}
                                    </v-icon>

                                    <div class="text-right">
                                    <div class="caption font-weight-bold">
                                        {{ weatherSummary.conditionText }}
                                    </div>
                                    <div class="caption">
                                        Vento: {{ weatherSummary.wind }} km/h
                                    </div>
                                    <div class="caption">
                                        Máx: {{ weatherSummary.max }}°C · Min: {{ weatherSummary.min }}°C
                                    </div>
                                    </div>
                                </div>
                            </v-card-title>

                            <v-card-text class="mt-2">
                                <v-btn
                                    rounded
                                    class="warning white--text mr-3 mb-3"
                                    @click="loadManobradores"
                                >
                                    <v-icon left>mdi-steering</v-icon>
                                    Diárias Manobrador
                                </v-btn>

                                <v-btn
                                    v-if="method==='create' && template != null"
                                    rounded
                                    class="black white--text mr-3 mb-3"
                                    @click="importTemplates"
                                >
                                    <v-icon left>mdi-paperclip</v-icon>
                                    Template
                                </v-btn>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" class="ma-0 pa-0 mt-2">
                        <v-tabs
                            v-if="!isMobile"
                            v-model="tab"
                            dark
                            fixed-tabs
                            background-color="primary"
                            icons-and-text                            
                        >
                            <v-tab :key="0" :disabled="$store.getters.hasRole(['parque'])">
                                Mão Obra
                                <v-icon>mdi-account-hard-hat</v-icon>
                            </v-tab>
                            <v-tab :key="1">
                                Equipamento
                                <v-icon>mdi-bulldozer</v-icon>
                            </v-tab>
                            <v-tab :key="2">
                                Material
                                <v-icon>mdi-archive</v-icon>
                            </v-tab>
                            <v-tab :key="3">
                                Subempreitadas
                                <v-icon>mdi-briefcase</v-icon>
                            </v-tab>
                            <v-tab :key="4" class="text-caption">
                                Diárias KPI
                                <v-icon>mdi-hard-hat</v-icon>
                            </v-tab>
                            <v-tab :key="5">
                                    Observações
                                <v-badge
                                    v-if="fields.obsMO != null || fields.obsEQ != null"
                                    color="error"
                                    dot
                                    overlap
                                >
                                    <v-icon>mdi-eye</v-icon>
                                </v-badge>
                                <v-icon v-else>mdi-eye</v-icon>
                            </v-tab>
                        </v-tabs>
                        <v-tabs
                            v-else
                            v-model="tab"
                            dark
                            fixed-tabs
                            background-color="primary"
                            icons-and-text                            
                        >
                            <v-tab :key="0" class="text-caption">
                                Mão Obra
                                <v-icon>mdi-account-hard-hat</v-icon>
                            </v-tab>
                            <v-tab :key="1" class="text-caption">
                                Equipamento
                                <v-icon>mdi-bulldozer</v-icon>
                            </v-tab>
                            <v-tab :key="2" class="text-caption">
                                Material
                                <v-icon>mdi-archive</v-icon>
                            </v-tab>
                            <v-tab :key="3" class="text-caption">
                                Subempreitadas
                                <v-icon>mdi-briefcase</v-icon>
                            </v-tab>
                            <v-tab :key="4" class="text-caption">
                                Diárias KPI
                                <v-icon>mdi-hard-hat</v-icon>
                            </v-tab>
                            <v-tab :key="5" class="text-caption">
                                Observações
                                <v-icon>mdi-eye</v-icon>
                            </v-tab>
                        </v-tabs>
                        <v-tabs-items v-model="tab" touchless>
                            <v-tab-item>                                
                                <div @touchstart.stop @touchmove.stop>
                                    <!-- Mão Obra Interna -->
                                    <v-container fluid class="ma-0 pa-0">
                                        <v-row no-gutters>
                                            <v-col cols="12" class="ma-0 pa-0">
                                                <v-card class="elevation-0">
                                                    <v-card-title class="d-none d-lg-block warning white--text" v-if="!isMobile">
                                                        Mão Obra Interna
                                                        <div>
                                                            <v-btn v-if="$store.getters.hasPermission(['super', 'diaries.import'])" x-small class="primary" elevation="0" @click="triggerFileInput">
                                                                Importar Excel
                                                            </v-btn>
                                                            <input  v-if="$store.getters.hasPermission(['super', 'diaries.import'])"
                                                                type="file" 
                                                                ref="fileInput" 
                                                                @change="handleFileUpload" 
                                                                accept=".xls,.xlsx" 
                                                                style="display: none" 
                                                            />
                                                            <v-btn
                                                                v-if="$store.getters.hasPermission(['super', 'diaries.import'])"
                                                                x-small
                                                                class="error ml-3"
                                                                elevation="0"
                                                                :href="'/templates/diarias/mo.xlsx'"
                                                                download="template_mo.xlsx"
                                                                >
                                                                Exportar template
                                                            </v-btn>
                                                        </div>
                                                    </v-card-title>
                                                    <v-card-title v-else class="d-block d-lg-none warning white--text text-caption text-center">
                                                        Mão Obra Interna
                                                        <div>
                                                            <v-btn v-if="$store.getters.hasPermission(['super', 'diaries.import'])" x-small class="primary" elevation="0" @click="triggerFileInput">
                                                                Importar Excel
                                                            </v-btn>
                                                            <input  v-if="$store.getters.hasPermission(['super', 'diaries.import'])"
                                                                type="file" 
                                                                ref="fileInput" 
                                                                @change="handleFileUpload" 
                                                                accept=".xls,.xlsx" 
                                                                style="display: none" 
                                                            />
                                                            <v-btn
                                                                v-if="$store.getters.hasPermission(['super', 'diaries.import'])"
                                                                x-small
                                                                class="error ml-3"
                                                                elevation="0"
                                                                :href="'/templates/diarias/mo.xlsx'"
                                                                download="template_mo.xlsx"
                                                                >
                                                                Exportar template
                                                            </v-btn>
                                                        </div>
                                                    </v-card-title>
                                                    <v-card-text class="ma-0 pa-0">
                                                        <v-simple-table v-if="!isMobile" class="ma-0 pa-0" fixed-header>
                                                            <template v-slot:default>
                                                                <thead>
                                                                    <tr>
                                                                        <th width="2%"></th>
                                                                        <th class="text-left" width="25%">
                                                                            Nome
                                                                        </th>
                                                                        <th class="text-left" width="25%">
                                                                            Frente Obra
                                                                        </th>
                                                                        <th class="text-left" width="20%">
                                                                            Classe
                                                                        </th>
                                                                        <th class="text-left" width="10%">
                                                                            HN
                                                                        </th>
                                                                        <th class="text-left" width="10%">
                                                                            HExt
                                                                        </th>
                                                                        <th v-if="$store.getters.hasRole(['super', 'administrativo', 'engenheiro'])" width="8%">
                                                                            Faturar
                                                                        </th>
                                                                        <th class="text-left" width="2%">
                                                                            Opções
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr
                                                                        v-for="(item, index) in computeFuncionariosInternos" :key="index+10001"
                                                                        :class="[item.id.includes('non-editable') ? 'my-orange' : (verificarCustosLinha(item) == true ? '' : 'my-grey'), integrationErrorClass(item)]"
                                                                    >
                                                                        <td>                                                                        
                                                                            <span v-if="item.fault_type_id != null">
                                                                                <v-icon>mdi-clock-out</v-icon>
                                                                            </span>
                                                                        </td>
                                                                        <td>         
                                                                            <validation-provider v-if="toEditRow(item)" v-slot="{ errors }" vid="funcionario" name="funcionario" rules="required">                                                           
                                                                                <Tooltip
                                                                                    color="primary"
                                                                                    :text="especialidadeMapGeral['interno_' + item.funcionario] || ''"
                                                                                    top
                                                                                >
                                                                                    <v-autocomplete
                                                                                        class="mt-4"
                                                                                        :value="item.funcionario"
                                                                                        :items="getFuncionariosInternosDisponiveis(item.funcionario)"
                                                                                        :item-text="item => item.Codigo + ' - ' + item.Nome"
                                                                                        item-value="Codigo"
                                                                                        label="Funcionário"
                                                                                        :error-messages="errors"
                                                                                        dense outlined
                                                                                        @change="val => updateField(item, 'funcionario', val, 'i')"
                                                                                    />
                                                                                </Tooltip>
                                                                            </validation-provider>                                                                        
                                                                            <Tooltip
                                                                                v-else
                                                                                color="primary"
                                                                                :text="item.especialidade"
                                                                                top
                                                                            >
                                                                                <v-text-field
                                                                                    dense outlined
                                                                                    class="mt-4"
                                                                                    label="Funcionario"
                                                                                    :disabled="true"
                                                                                    :value="item.funcionario + ' - ' + item.Nome"
                                                                                />
                                                                            </Tooltip>
                                                                        </td>
                                                                        <td>
                                                                            <div v-if="showFrente(item)">
                                                                                <validation-provider v-slot="{ errors }" vid="Frentes" name="Frentes" rules="required">
                                                                                    <v-autocomplete
                                                                                        :value="item.frente_id"
                                                                                        :items="frentes_obra"
                                                                                        outlined
                                                                                        dense
                                                                                        :disabled="item.id.includes('non-editable')"
                                                                                        class="mt-4"
                                                                                        :item-text="ob => getText(ob)"
                                                                                        item-value="ID"
                                                                                        :error-messages="errors"
                                                                                        label="Frentes de Obra *"
                                                                                        @change="val => updateField(item, 'frente_id', val, 'i')"
                                                                                    />
                                                                                </validation-provider>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div v-if="showClasse(item)">
                                                                                <validation-provider v-slot="{ errors }" vid="Classes" name="Classes" rules="required">
                                                                                    <v-autocomplete
                                                                                        :value="item.classe_id"
                                                                                        :items="item.available_classes"
                                                                                        class="mt-4"
                                                                                        :disabled="item.id.includes('non-editable')"
                                                                                        outlined
                                                                                        dense
                                                                                        :item-text="ob => ob.Descricao"
                                                                                        item-value="ID"
                                                                                        :error-messages="errors"
                                                                                        label="Classe *"
                                                                                        @change="val => updateField(item, 'classe_id', val, 'i')"
                                                                                    />
                                                                                </validation-provider>
                                                                            </div>
                                                                        </td>
                                                                        <td :colspan="item.add_fault ? 3 : 1">
                                                                            <v-text-field
                                                                                v-show="!item.add_fault"
                                                                                class="mt-4"
                                                                                label="HN"
                                                                                outlined
                                                                                dense
                                                                                :disabled="item.id.includes('non-editable')"
                                                                                placeholder="Insira HN"
                                                                                :value="item.hn"
                                                                                type="text"
                                                                                inputmode="decimal"
                                                                                pattern="[0-9.]*"
                                                                                @keydown.native="onlyNumericOneDot"
                                                                                @input="val => sanitizeNumericOneDot(val, item, 'hn')" 
                                                                            />
                                                                            <v-autocomplete
                                                                                v-if="item.add_fault"
                                                                                v-model="item.fault_type_id"
                                                                                :items="fault_types"
                                                                                :item-text="item=>item.description"
                                                                                class="mt-4"
                                                                                item-value="id"
                                                                                clearable
                                                                                outlined
                                                                                dense
                                                                                label="Tipo de falta *"
                                                                                @click:clear="clearDataFalta(item)"  
                                                                            />
                                                                        </td>
                                                                        <td v-show="!item.add_fault">
                                                                            <validation-provider v-slot="{ errors }" vid="HExt" name="HExt" rules="required">
                                                                                <v-text-field
                                                                                    class="mt-4"
                                                                                    label="HExt"
                                                                                    outlined
                                                                                    dense
                                                                                    :disabled="item.id.includes('non-editable')"
                                                                                    placeholder="Insira HExt"
                                                                                    :value="item.hext"
                                                                                    type="text"
                                                                                    inputmode="decimal"
                                                                                    pattern="[0-9.]*"
                                                                                    @keydown.native="onlyNumericOneDot"
                                                                                    @input="sanitizeNumericOneDot($event, item, 'hext')"
                                                                                />
                                                                            </validation-provider>
                                                                        </td>
                                                                        <td v-show="!item.add_fault" v-if="$store.getters.hasRole(['super', 'engenheiro', 'administrativo'])">
                                                                            <v-autocomplete                                                                            
                                                                                label="Faturar"
                                                                                :items="item.faturar_values"
                                                                                :value="item.faturar"
                                                                                @change="val => updateField(item, 'faturar', val, 'i')"
                                                                                :disabled="item.id.includes('non-editable')"
                                                                                dense
                                                                                outlined
                                                                                class="mt-4"
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <v-speed-dial
                                                                                v-if="!item.id.includes('non-editable')"
                                                                                v-model="fabOptions[item.id]"
                                                                                direction="left"
                                                                                :style="editorModal ? 'position: absolute; transform: translateY(-50%);' : 'position: absolute; transform: translateY(-50%); z-index: 998;'"
                                                                            >
                                                                                <template v-slot:activator>                                                                                
                                                                                    <v-btn
                                                                                        v-if="fabOptions[item.id]" 
                                                                                        :class="item.local == null || item.local == '' ? 'primary elevation-0' : 'primary elevation-10'" fab small @click="openEditorModal(item, 'funcionario_interno')">
                                                                                        <v-icon v-if="item.local == null || item.local == ''">mdi-note-edit</v-icon>
                                                                                        <v-badge
                                                                                            v-else
                                                                                            color="orange"
                                                                                            dot
                                                                                            overlap
                                                                                        >
                                                                                            <v-icon>mdi-note-plus</v-icon>
                                                                                        </v-badge>
                                                                                    </v-btn>
                                                                                    <v-badge
                                                                                        :color="(item.local == null || item.local == '') ? 'transparent' : 'primary'"
                                                                                        dot
                                                                                        overlap
                                                                                    >
                                                                                        <v-btn
                                                                                            v-if="!fabOptions[item.id]"
                                                                                            small
                                                                                            v-model="fabOptions[item.id]"
                                                                                            color="warning"
                                                                                            dark
                                                                                            fab
                                                                                        >
                                                                                            <v-icon>
                                                                                                mdi-dots-horizontal
                                                                                            </v-icon>
                                                                                        </v-btn>
                                                                                    </v-badge>
                                                                                    <v-btn
                                                                                        v-if="fabOptions[item.id]"
                                                                                        fab
                                                                                        dark
                                                                                        small
                                                                                        color="error"
                                                                                        @click="deleteFuncionarioInterno(item.id)"
                                                                                    >
                                                                                        <v-icon>mdi-delete</v-icon>
                                                                                    </v-btn>
                                                                                    <v-btn
                                                                                        v-if="fabOptions[item.id]"
                                                                                        fab
                                                                                        dark
                                                                                        small
                                                                                        color="#FB8C00"
                                                                                        @click="item.add_fault = true; item.hn = 0; item.hext = 0;"
                                                                                    >
                                                                                        <v-icon>mdi-clock-out</v-icon>
                                                                                    </v-btn>
                                                                                </template>
                                                                                
                                                                            </v-speed-dial>
                                                                        </td>
                                                                    
                                                                    </tr>
                                                                </tbody>
                                                            </template>
                                                        </v-simple-table>

                                                        <v-data-iterator
                                                            :items="fields.funcionariosInternos"
                                                            item-key="id"
                                                            :items-per-page="-1"
                                                            hide-default-footer
                                                            v-else
                                                            class="ma-0 pa-0"
                                                        >
                                                        <template v-slot:default="{ items }">
                                                            <v-row class="ma-0 pa-0">
                                                                <v-col
                                                                    class="ma-0 pa-0"
                                                                    v-for="(item, index) in items"
                                                                    :key="index+200000"
                                                                    cols="12"
                                                                >
                                                                    <v-card elevation="0" :class="['mt-2 rounded-lg ma-0 pa-0', integrationErrorClass(item)]" outlined>
                                                                        <v-list
                                                                            dense
                                                                        >
                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center justify-center">
                                                                                    <span style="margin: auto; display: block" v-if="item.fault_type_id != null">
                                                                                        <v-icon>mdi-clock-out</v-icon>
                                                                                    </span>
                                                                                </v-list-item-content>        
                                                                            </v-list-item>
                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <validation-provider v-if="toEditRow(item)" v-slot="{ errors }" vid="funcionario" name="funcionario" rules="required">
                                                                                        <v-autocomplete
                                                                                            class="mt-4"
                                                                                            outlined
                                                                                            dense
                                                                                            :disabled="item.id.includes('non-editable')"
                                                                                            :items="funcionarios_internos"
                                                                                            :item-text="item => item.Codigo + ' - ' + item.Nome"
                                                                                            item-value="Codigo"
                                                                                            label="Funcionário"
                                                                                            :error-messages="errors"
                                                                                            :value="item.funcionario"
                                                                                            @change="val => updateField(item, 'funcionario', val, 'i')"
                                                                                        />
                                                                                    </validation-provider>
                                                                                    <v-text-field
                                                                                        v-else
                                                                                        dense outlined
                                                                                        class="mt-4"
                                                                                        label="Funcionario"
                                                                                        :disabled="true"
                                                                                        :value="item.funcionario + ' - ' + item.Nome"
                                                                                    />
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <div v-if="showFrente(item)">
                                                                                        <validation-provider v-slot="{ errors }" vid="Frentes" name="Frentes" rules="required">
                                                                                            <v-autocomplete
                                                                                                :value="item.frente_id"
                                                                                                :items="frentes_obra"
                                                                                                outlined
                                                                                                dense
                                                                                                class="mt-4"
                                                                                                :item-text="ob => getText(ob)"
                                                                                                :disabled="item.id.includes('non-editable')"
                                                                                                item-value="ID"
                                                                                                :error-messages="errors"
                                                                                                label="Frentes de Obra *"
                                                                                                @change="val => updateField(item, 'frente_id', val, 'i')"
                                                                                            />
                                                                                        </validation-provider>
                                                                                    </div>
                                                                                    <span v-else>{{item.frente_obra == null ? '-' : item.frente_obra}}</span>
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <div v-if="showClasse(item)">
                                                                                        <validation-provider v-slot="{ errors }" vid="Classes" name="Classes" rules="required">
                                                                                            <v-autocomplete
                                                                                                :value="item.classe_id"
                                                                                                :items="item.available_classes"
                                                                                                class="mt-4"
                                                                                                :disabled="item.id.includes('non-editable')"
                                                                                                outlined
                                                                                                dense
                                                                                                :item-text="ob => ob.Descricao"
                                                                                                item-value="ID"
                                                                                                :error-messages="errors"
                                                                                                label="Classe *"
                                                                                                @change="val => updateField(item, 'classe_id', val, 'i')"
                                                                                            />
                                                                                        </validation-provider>
                                                                                    </div>
                                                                                    <span v-else>{{item.classe_obra == null ? '-' : item.classe_obra}}</span>
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <v-text-field
                                                                                        v-if="!item.add_fault"
                                                                                        class="mt-4"
                                                                                        label="HN"
                                                                                        outlined
                                                                                        dense
                                                                                        :disabled="item.id.includes('non-editable')"
                                                                                        placeholder="Insira HN"
                                                                                        :value="item.hn"
                                                                                        type="text"
                                                                                        inputmode="decimal"
                                                                                        pattern="[0-9.]*"
                                                                                        @keydown.native="onlyNumericOneDot"
                                                                                        @input="sanitizeNumericOneDot($event, item, 'hn')"
                                                                                    />
                                                                                    <v-autocomplete
                                                                                        v-if="item.add_fault"
                                                                                        v-model="item.fault_type_id"
                                                                                        :items="fault_types"
                                                                                        :item-text="item=>item.description"
                                                                                        class="mt-4"
                                                                                        item-value="id"
                                                                                        clearable
                                                                                        outlined
                                                                                        dense
                                                                                        label="Tipo de falta *"
                                                                                        @click:clear="clearDataFalta(item)"  
                                                                                    />
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>

                                                                            <v-list-item v-show="!item.add_fault">
                                                                                <v-list-item-content class="align-center">                                                                                
                                                                                    <validation-provider v-slot="{ errors }" vid="HExt" name="HExt" rules="required">
                                                                                        <v-text-field
                                                                                            class="mt-4"
                                                                                            label="HExt"
                                                                                            outlined
                                                                                            dense
                                                                                            :disabled="item.id.includes('non-editable')"
                                                                                            placeholder="Insira HExt"
                                                                                            :value="item.hext"
                                                                                            type="text"
                                                                                            :error-messages="errors"
                                                                                            inputmode="decimal"
                                                                                            pattern="[0-9.]*"
                                                                                            @keydown.native="onlyNumericOneDot"
                                                                                            @input="sanitizeNumericOneDot($event, item, 'hext')"
                                                                                        />
                                                                                    </validation-provider>
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <v-btn v-if="!item.id.includes('non-editable')" :class="item.local == null || item.local == '' ? 'primary elevation-0' : 'primary elevation-10'" rounded small @click="openEditorModal(item, 'funcionario_interno')">
                                                                                        <v-icon small v-if="item.local == null || item.local == ''">mdi-plus</v-icon>
                                                                                        <v-badge
                                                                                            v-else
                                                                                            color="orange"
                                                                                            dot
                                                                                            overlap
                                                                                        >
                                                                                            <v-icon small>mdi-eye</v-icon>
                                                                                        </v-badge>
                                                                                        Nota
                                                                                    </v-btn>
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <v-row>
                                                                                        <v-col cols="12" md="6">                                                                                        
                                                                                            <v-btn
                                                                                                dark
                                                                                                small
                                                                                                color="error"
                                                                                                @click="deleteFuncionarioInterno(item.id)"
                                                                                            >
                                                                                                <v-icon small>mdi-delete</v-icon> Remover
                                                                                            </v-btn>
                                                                                        </v-col>
                                                                                        <v-col cols="12" md="6">
                                                                                            <v-btn
                                                                                                dark
                                                                                                small
                                                                                                color="#FB8C00"
                                                                                                @click="item.add_fault = true; item.hn = 0; item.hext = 0;"
                                                                                            >
                                                                                                <v-icon>mdi-clock-out</v-icon>Faltas
                                                                                            </v-btn>
                                                                                        </v-col>
                                                                                    </v-row>
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>
                                                                        </v-list>
                                                                    </v-card>
                                                                </v-col>
                                                            </v-row>
                                                        </template>
                                                    </v-data-iterator>
                                                        

                                                    <v-row justify="center" class="mt-4">
                                                        <v-col cols="4" class="text-left">
                                                            <v-btn
                                                            class="error"
                                                            small
                                                            fab
                                                            :disabled="paginacaoInterna.menor === 0"
                                                            @click="paginacaoInterna.menor = Math.max(0, paginacaoInterna.menor - step); paginacaoInterna.maior = Math.max(step, paginacaoInterna.maior - step)"
                                                            >
                                                            <v-icon>mdi-chevron-left</v-icon>
                                                            </v-btn>
                                                        </v-col>

                                                        <v-col cols="4" align="center" class="text-center">
                                                            <v-btn :disabled="addInternoDisabled" fab @click="addFuncionarioInterno" class="primary">
                                                                <v-icon>mdi-plus</v-icon>
                                                            </v-btn>
                                                        </v-col>

                                                        <v-col cols="4" class="text-right">
                                                            <v-btn
                                                            class="error"
                                                            small
                                                            fab
                                                            :disabled="paginacaoInterna.maior >= totalFuncionariosInternos"
                                                            @click="paginacaoInterna.menor += step; paginacaoInterna.maior += step"
                                                            >
                                                            <v-icon>mdi-chevron-right</v-icon>
                                                            </v-btn>
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12" class="text-center grey--text text-caption">
                                                            Página {{ currentPageInterna }} de {{ totalPagesInterna }}
                                                        </v-col>
                                                    </v-row>

                                                    </v-card-text>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                    <!-- Mão Obra Interna -->

                                    <!-- Mão Obra Externa -->
                                    <v-container fluid>
                                        <v-row no-gutters>
                                            <v-col cols="12">
                                                <v-card class="elevation-0">
                                                    <v-card-title v-if="!isMobile" class="d-none d-lg-block warning white--text">
                                                        Mão Obra Externa 
                                                        <div>
                                                            <v-btn v-if="$store.getters.hasPermission(['super', 'diaries.import'])" x-small class="primary" elevation="0" @click="triggerFileInputExternal">
                                                                Importar Excel
                                                            </v-btn>
                                                            <input v-if="$store.getters.hasPermission(['super', 'diaries.import'])"
                                                                type="file" 
                                                                ref="fileInputExternal" 
                                                                @change="handleFileUploadExternal" 
                                                                accept=".xls,.xlsx" 
                                                                style="display: none" 
                                                            />                                                        
                                                            <v-btn
                                                                v-if="$store.getters.hasPermission(['super', 'diaries.import'])"
                                                                x-small
                                                                class="error ml-3"
                                                                elevation="0"
                                                                :href="'/templates/diarias/mo.xlsx'"
                                                                download="template_mo.xlsx"
                                                                >
                                                                Exportar template
                                                            </v-btn>
                                                            <v-btn
                                                                style="float: right;"
                                                                x-small
                                                                class="primary"
                                                                @click="entitySelect = false;entitySelect = true"
                                                            ><v-icon small>mdi-magnify</v-icon>Entidade
                                                            </v-btn>
                                                        </div>
                                                    </v-card-title>
                                                    <v-card-title v-else class="d-block d-lg-none warning white--text text-caption text-center">
                                                        Mão Obra Externa
                                                        <div>
                                                            <v-btn v-if="$store.getters.hasPermission(['super', 'diaries.import'])" x-small class="primary" elevation="0" @click="triggerFileInputExternal">
                                                                Importar Excel
                                                            </v-btn>
                                                            <input v-if="$store.getters.hasPermission(['super', 'diaries.import'])"
                                                                type="file" 
                                                                ref="fileInputExternal" 
                                                                @change="handleFileUploadExternal" 
                                                                accept=".xls,.xlsx" 
                                                                style="display: none" 
                                                            />
                                                            <v-btn
                                                                v-if="$store.getters.hasPermission(['super', 'diaries.import'])"
                                                                x-small
                                                                class="error ml-3"
                                                                elevation="0"
                                                                :href="'/templates/diarias/mo.xlsx'"
                                                                download="template_mo.xlsx"
                                                                >
                                                                Exportar template
                                                            </v-btn>
                                                            <v-btn
                                                                style="float: right;"
                                                                x-small
                                                                class="primary"
                                                                @click="entitySelect = false;entitySelect = true"
                                                            ><v-icon small>mdi-magnify</v-icon>Entidade
                                                            </v-btn>
                                                        </div>
                                                    </v-card-title>
                                                    <v-card-text class="ma-0 pa-0">
                                                        <v-simple-table v-if="!isMobile" class="d-none d-lg-block ma-0 pa-0" fixed-header>
                                                            <template v-slot:default>
                                                                <thead>
                                                                    <tr>
                                                                        <th class="text-left" width="15%">
                                                                            Entidade
                                                                        </th>
                                                                        <th class="text-left" width="19%">
                                                                            Nome
                                                                        </th>
                                                                        <th class="text-left" width="19%">
                                                                            Frente Obra
                                                                        </th>
                                                                        <th class="text-left" width="19%">
                                                                            Classe
                                                                        </th>
                                                                        <th class="text-left" width="9%">
                                                                            HN
                                                                        </th>
                                                                        <th class="text-left" width="9%">
                                                                            HExt
                                                                        </th>
                                                                        <th v-if="$store.getters.hasRole(['super', 'administrativo', 'engenheiro'])" width="7%">
                                                                            Faturar
                                                                        </th>
                                                                        <th class="text-left" width="2%">
                                                                            Opções
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr
                                                                        v-for="(item, index) in computeFuncionariosExternos" :key="index + 800000"
                                                                        :class="[verificarCustosLinha(item) == true ? 'mt-4' : 'my-grey mt-4', integrationErrorClass(item)]"
                                                                    >
                                                                        <td>         
                                                                            <v-text-field 
                                                                                v-if="item.entidade != null"
                                                                                class="mt-4"
                                                                                readonly
                                                                                dense outlined
                                                                                :value="item.entidade.Codigo + ' - ' + item.entidade.Nome"
                                                                                label="Entidade"
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <validation-provider v-if="toEditRow(item)" v-slot="{ errors }" vid="funcionario_externo" name="funcionario_externo" rules="required">                                                                                                                          
                                                                                <Tooltip
                                                                                    color="primary"
                                                                                    :text="especialidadeMapGeral['externo_' + item.funcionario] || ''"
                                                                                    top
                                                                                >
                                                                                    <v-autocomplete
                                                                                        class="mt-4"
                                                                                        outlined dense
                                                                                        :items="funcionarios_externos"
                                                                                        :item-text="item => item.Numero + ' - ' + item.Nome"
                                                                                        item-value="Numero"
                                                                                        label="Funcionário"
                                                                                        :error-messages="errors"
                                                                                        v-model="item.funcionario"
                                                                                        @change="changedExterno; calculateMaoObraCost(item, 'e')"
                                                                                    />
                                                                                </Tooltip>
                                                                            </validation-provider>
                                                                            <Tooltip
                                                                                v-else
                                                                                color="primary"
                                                                                :text="item.especialidade"
                                                                                top
                                                                            >
                                                                                <v-text-field
                                                                                    dense outlined
                                                                                    class="mt-4"
                                                                                    label="Funcionario"
                                                                                    :disabled="true"
                                                                                    :value="item.funcionario + ' - ' + item.Nome"
                                                                                />
                                                                            </Tooltip>
                                                                        </td>
                                                                        <td>
                                                                            <div v-if="showFrente(item)">
                                                                                <validation-provider v-slot="{ errors }" vid="Frentes" name="Frentes" rules="required">
                                                                                    <v-autocomplete                                                                                
                                                                                        v-model="item.frente_id"
                                                                                        :items="frentes_obra"
                                                                                        outlined dense
                                                                                        class="mt-4"
                                                                                        :item-text="ob => getText(ob)"
                                                                                        item-value="ID"
                                                                                        :error-messages="errors"
                                                                                        label="Frentes de Obra *"
                                                                                        @change="changeFrenteObraV2(item)"
                                                                                    />
                                                                                </validation-provider>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div v-if="showClasse(item)">
                                                                                <validation-provider v-slot="{ errors }" vid="Classes" name="Classes" rules="required">
                                                                                    <v-autocomplete
                                                                                        v-model="item.classe_id"
                                                                                        :items="item.available_classes"
                                                                                        class="mt-4"
                                                                                        outlined dense
                                                                                        :item-text="ob => ob.Descricao"
                                                                                        item-value="ID"
                                                                                        :error-messages="errors"
                                                                                        label="Classe *"
                                                                                    />
                                                                                </validation-provider>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <v-text-field
                                                                                class="mt-4"
                                                                                label="HN"
                                                                                outlined
                                                                                dense
                                                                                :disabled="item.id.includes('non-editable')"
                                                                                placeholder="Insira HN"
                                                                                :value="item.hn"
                                                                                type="text"
                                                                                inputmode="decimal"
                                                                                pattern="[0-9.]*"
                                                                                @keydown.native="onlyNumericOneDot"
                                                                                @input="sanitizeNumericOneDot($event, item, 'hn', true)"
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <validation-provider v-slot="{ errors }" vid="HExt" name="HExt" rules="required">
                                                                                <v-text-field
                                                                                    class="mt-4"
                                                                                    label="HExt"
                                                                                    outlined
                                                                                    dense
                                                                                    :disabled="item.id.includes('non-editable')"
                                                                                    placeholder="Insira HExt"
                                                                                    :value="item.hext"
                                                                                    type="text"
                                                                                    inputmode="decimal"
                                                                                    pattern="[0-9.]*"
                                                                                    @keydown.native="onlyNumericOneDot"
                                                                                    @input="sanitizeNumericOneDot($event, item, 'hext', true)"
                                                                                />
                                                                            </validation-provider>
                                                                        </td>

                                                                        <td v-if="$store.getters.hasRole(['super', 'engenheiro', 'administrativo'])">
                                                                            <v-autocomplete
                                                                                label="Faturar"
                                                                                :items="item.faturar_values"
                                                                                v-model="item.faturar"
                                                                                dense outlined
                                                                                class="mt-4"
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <v-speed-dial
                                                                                v-model="fabOptions[item.id]"
                                                                                direction="left"
                                                                                style="position: absolute; transform: translateY(-50%)"
                                                                            >
                                                                                <template v-slot:activator>                                                                                
                                                                                    <v-btn
                                                                                        v-if="fabOptions[item.id]" 
                                                                                        :class="item.local == null || item.local == '' ? 'primary elevation-0' : 'primary elevation-10'" fab small @click="openEditorModal(item, 'funcionario_externo')">
                                                                                        <v-icon v-if="item.local == null || item.local == ''">mdi-note-edit</v-icon>
                                                                                        <v-badge
                                                                                            v-else
                                                                                            color="orange"
                                                                                            dot
                                                                                            overlap
                                                                                        >
                                                                                            <v-icon>mdi-note-plus</v-icon>
                                                                                        </v-badge>
                                                                                    </v-btn>
                                                                                    <v-badge
                                                                                        :color="(item.local == null || item.local == '') ? 'transparent' : 'primary'"
                                                                                        dot
                                                                                        overlap
                                                                                    >
                                                                                        <v-btn
                                                                                            v-if="!fabOptions[item.id]"
                                                                                            small
                                                                                            v-model="fabOptions[item.id]"
                                                                                            color="warning"
                                                                                            dark
                                                                                            fab
                                                                                        >
                                                                                            <v-icon>
                                                                                                mdi-dots-horizontal
                                                                                            </v-icon>
                                                                                        </v-btn>
                                                                                    </v-badge>
                                                                                    <v-btn
                                                                                        v-if="fabOptions[item.id]"
                                                                                        fab
                                                                                        dark
                                                                                        small
                                                                                        color="error"
                                                                                        @click="deleteFuncionarioExterno(item.id)"
                                                                                    >
                                                                                        <v-icon>mdi-delete</v-icon>
                                                                                    </v-btn>
                                                                                </template>
                                                                                
                                                                            </v-speed-dial>
                                                                        </td>
                                                                    
                                                                    </tr>
                                                                </tbody>
                                                            </template>
                                                        </v-simple-table>

                                                        <v-data-iterator
                                                            :items="fields.funcionariosExternos"
                                                            item-key="id"
                                                            :items-per-page="-1"
                                                            hide-default-footer
                                                            class="d-block d-lg-none"
                                                            v-else
                                                        >
                                                        <template v-slot:default="{ items }">
                                                            <v-row>
                                                                <v-col
                                                                    v-for="(item, index) in items"
                                                                    :key="index+30000"
                                                                    cols="12"
                                                                >
                                                                    <v-card elevation="0" :class="['mt-2 rounded-lg', integrationErrorClass(item)]" outlined>
                                                                        <v-list
                                                                            dense
                                                                        >
                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <v-text-field 
                                                                                        class="mt-4"
                                                                                        v-if="item.entidade != null"
                                                                                        readonly
                                                                                        dense outlined
                                                                                        :value="item.entidade.Codigo + ' - ' + item.entidade.Nome"
                                                                                        label="Entidade"
                                                                                    />
                                                                                </v-list-item-content>
                                                                            </v-list-item>
                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <validation-provider v-if="toEditRow(item)" v-slot="{ errors }" vid="funcionario_externo" name="funcionario_externo" rules="required">                                                                   
                                                                                        <v-autocomplete
                                                                                            class="mt-4"
                                                                                            outlined dense
                                                                                            :items="funcionarios_externos"
                                                                                            :item-text="item => item.Numero + ' - ' + item.Nome"
                                                                                            item-value="Numero"
                                                                                            label="Funcionário"
                                                                                            :error-messages="errors"
                                                                                            v-model="item.funcionario"
                                                                                            @change="changedExterno;calculateMaoObraCost(item, 'e')"
                                                                                        />
                                                                                    </validation-provider>
                                                                                    <v-text-field
                                                                                        v-else
                                                                                        dense outlined
                                                                                        class="mt-4"
                                                                                        label="Funcionario"
                                                                                        :disabled="true"
                                                                                        :value="item.funcionario + ' - ' + item.Nome"
                                                                                    />
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <div v-if="showFrente(item)">
                                                                                        <validation-provider v-slot="{ errors }" vid="Frentes" name="Frentes" rules="required">
                                                                                            <v-autocomplete                                                                                
                                                                                                v-model="item.frente_id"
                                                                                                :items="frentes_obra"
                                                                                                outlined dense
                                                                                                class="mt-4"
                                                                                                :item-text="ob => getText(ob)"
                                                                                                item-value="ID"
                                                                                                :error-messages="errors"
                                                                                                label="Frentes de Obra *"
                                                                                                @change="changeFrenteObraV2(item)"
                                                                                            />
                                                                                        </validation-provider>
                                                                                    </div>
                                                                                </v-list-item-content>
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <div v-if="showClasse(item)">
                                                                                        <validation-provider v-slot="{ errors }" vid="Classes" name="Classes" rules="required">
                                                                                            <v-autocomplete
                                                                                                v-model="item.classe_id"
                                                                                                :items="item.available_classes"
                                                                                                class="mt-4"
                                                                                                outlined dense
                                                                                                :item-text="ob => ob.Descricao"
                                                                                                item-value="ID"
                                                                                                :error-messages="errors"
                                                                                                label="Classe *"
                                                                                            />
                                                                                        </validation-provider>
                                                                                    </div>
                                                                                </v-list-item-content>
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <v-text-field
                                                                                        class="mt-4"
                                                                                        label="HN"
                                                                                        outlined
                                                                                        dense
                                                                                        :disabled="item.id.includes('non-editable')"
                                                                                        placeholder="Insira HN"
                                                                                        :value="item.hn"
                                                                                        type="text"
                                                                                        inputmode="decimal"
                                                                                        pattern="[0-9.]*"
                                                                                        @keydown.native="onlyNumericOneDot"
                                                                                        @input="sanitizeNumericOneDot($event, item, 'hn', true)"
                                                                                    />
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">                                                                                
                                                                                    <validation-provider v-slot="{ errors }" vid="HExt" name="HExt" rules="required">
                                                                                        <v-text-field
                                                                                            class="mt-4"
                                                                                            label="HExt"
                                                                                            outlined
                                                                                            dense
                                                                                            :disabled="item.id.includes('non-editable')"
                                                                                            placeholder="Insira HExt"
                                                                                            :value="item.hext"
                                                                                            type="text"
                                                                                            inputmode="decimal"
                                                                                            pattern="[0-9.]*"
                                                                                            @keydown.native="onlyNumericOneDot"
                                                                                            @input="sanitizeNumericOneDot($event, item, 'hext', true)"
                                                                                        />
                                                                                    </validation-provider>
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>


                                                                            <v-list-item v-if="$store.getters.hasRole(['super', 'engenheiro', 'administrativo'])">
                                                                                <v-list-item-content>
                                                                                    <v-autocomplete
                                                                                        label="Faturar"
                                                                                        :items="item.faturar_values"
                                                                                        v-model="item.faturar"
                                                                                        dense outlined
                                                                                        class="mt-4"
                                                                                    />
                                                                                </v-list-item-content>
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <v-btn :class="item.local == null || item.local == '' ? 'primary elevation-0' : 'primary elevation-10'" rounded small @click="openEditorModal(item, 'funcionario_externo')">
                                                                                        <v-icon small v-if="item.local == null || item.local == ''">mdi-plus</v-icon>
                                                                                        <v-badge
                                                                                            v-else
                                                                                            color="orange"
                                                                                            dot
                                                                                            overlap
                                                                                        >
                                                                                            <v-icon small>mdi-eye</v-icon>
                                                                                        </v-badge>
                                                                                        Nota
                                                                                    </v-btn>
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>

                                                                            <v-list-item>
                                                                                <v-list-item-content class="align-center">
                                                                                    <v-btn
                                                                                        dark
                                                                                        small
                                                                                        color="error"
                                                                                        @click="deleteFuncionarioExterno(item.id)"
                                                                                    >
                                                                                        <v-icon small>mdi-delete</v-icon> Remover
                                                                                    </v-btn>
                                                                                </v-list-item-content>                                                                            
                                                                            </v-list-item>
                                                                        </v-list>
                                                                    </v-card>
                                                                </v-col>
                                                            </v-row>
                                                        </template>
                                                        </v-data-iterator>


                                                        <v-row justify="center" class="mt-4">
                                                            <v-col cols="4" class="text-left">
                                                                <v-btn
                                                                    class="error"
                                                                    small
                                                                    fab
                                                                    :disabled="paginacaoExterna.menor === 0"
                                                                    @click="paginacaoExterna.menor = Math.max(0, paginacaoExterna.menor - step); paginacaoExterna.maior = Math.max(step, paginacaoExterna.maior - step)"
                                                                >
                                                                    <v-icon>mdi-chevron-left</v-icon>
                                                                </v-btn>
                                                            </v-col>

                                                            <v-col cols="4" align="center" class="text-center">
                                                                <v-btn fab @click="addFuncionarioExterno" class="primary">
                                                                    <v-icon>mdi-plus</v-icon>
                                                                </v-btn>
                                                            </v-col>

                                                            <v-col cols="4" class="text-right">
                                                                <v-btn
                                                                    class="error"
                                                                    small
                                                                    fab
                                                                    :disabled="paginacaoExterna.maior >= totalFuncionariosExternos"
                                                                    @click="
                                                                        if (paginacaoExterna.menor + step < totalFuncionariosExternos) {
                                                                            paginacaoExterna.menor += step;
                                                                            paginacaoExterna.maior = Math.min(paginacaoExterna.menor + step, totalFuncionariosExternos);
                                                                        }
                                                                    "

                                                                >
                                                                    <v-icon>mdi-chevron-right</v-icon>
                                                                </v-btn>
                                                            </v-col>
                                                        </v-row>
                                                        <v-row>
                                                            <v-col cols="12" class="text-center grey--text text-caption">
                                                                Página {{ currentPageExterna }} de {{ totalPagesExterna }}
                                                            </v-col>
                                                        </v-row>
                                                    </v-card-text>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                    <!-- Mão Obra Externa -->
                                </div>
                            </v-tab-item>
                            <v-tab-item>
                                <div @touchstart.stop @touchmove.stop>
                                    <Equipments @loader="loader=!loader" ref="equipamentos" :diarias_manobrador="diarias_manobrador" @nota="openEditorModal" :loadedClasses="classes" :obraCodigo="(obras.find(o => o.Id == fields.obra_id) || {}).Codigo || null" :hideFrentes="hideFrentes" :frentes_obra="frentes_obra" :fields="fields" return-object/>
                                </div>
                            </v-tab-item>
                            <v-tab-item>
                                <div @touchstart.stop @touchmove.stop>
                                    <Materials @nota="openEditorModal" :obraCodigo="(obras.find(o => o.Id == fields.obra_id) || {}).Codigo || null" :loadedClasses="classes" :hideFrentes="hideFrentes" :frentes_obra="frentes_obra" :fields="fields" return-object/>
                                </div>
                            </v-tab-item>
                            <v-tab-item>
                                <div @touchstart.stop @touchmove.stop>
                                    <Subcontractors @nota="openEditorModal" :obraCodigo="(obras.find(o => o.Id == fields.obra_id) || {}).Codigo || null" :loadedClasses="classes" :hideFrentes="hideFrentes" :frentes_obra="frentes_obra" :fields="fields" @update:fields="atualizaSubempreitadas" return-object/>
                                </div>
                            </v-tab-item>
                            <v-tab-item>
                                <div @touchstart.stop @touchmove.stop>
                                    <DiaryKPI ref="diaria_kpi" :all_external_workers="funcionarios_externos" :all_workers="funcionarios_internos" :workers="computeFuncionariosInternos" :diary="diary_kpi" :method="diary_kpi == null ? 'create' : 'update'" :inside_diary="true" :fields_diary="fields"/>
                                </div>
                            </v-tab-item>
                            <v-tab-item>
                                <div @touchstart.stop @touchmove.stop>
                                    <Observations :fields="fields" return-object />
                                </div>
                            </v-tab-item>
                        </v-tabs-items>
                    </v-col>
                </v-row>
                <v-divider></v-divider>
                <v-row v-if="showContent">
                    <v-col cols="12" md="1" class="d-none d-lg-block">                                
                        <v-btn v-show="fields.obra_id != null" :disabled="tab == 0" @click="tab = tab - 1" small fab class="mt-4 primary">
                            <v-icon small>mdi-less-than</v-icon>
                        </v-btn>
                    </v-col>
                    <v-col cols="12" md="10" align="center">
                        <v-btn v-if="canSave == 4" rounded class="success mt-4" disabled>
                            Gravar
                        </v-btn>
                        <v-btn v-else rounded class="success mt-4" :disabled="invalid" type="submit">
                            Gravar
                        </v-btn>
                    </v-col>
                    <v-col cols="12" md="1" class="d-none d-lg-block">                           
                        <v-btn v-show="fields.obra_id != null" :disabled="tab == 4" @click="tab = tab + 1" small fab class="mt-4 primary">
                            <v-icon small>mdi-greater-than</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
        </validation-observer>

    <v-dialog
        v-model="entitySelect"
        fullscreen
    >
        <v-card class="elevation-0 ma-0 pa-0" v-if="entitySelect">
            <v-toolbar
                dark
                color="primary"
            >
                <v-toolbar-title>Funcionários por entidade</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                <v-btn
                    dark
                    text
                    @click="entitySelect = false"
                >
                    X
                </v-btn>
            </v-toolbar-items>
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text class="mt-4 d-none d-lg-block">
                <v-row>
                    <v-col cols="12" md="6" v-if="hideFrentes == false" >
                        <v-autocomplete                                                                                                       
                            v-model="entitySelectedFrente"
                            :items="frentes_obra"
                            outlined dense
                            class="mt-4"
                            :item-text="ob => getText(ob)"
                            item-value="ID"
                            label="Frentes de Obra *"
                            @change="changeFrenteObraV2Entity"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-autocomplete
                            v-model="entitySelectedClasse"
                            :items="entityClasses"
                            class="mt-4"
                            outlined dense
                            :item-text="ob => ob.Descricao"
                            item-value="ID"
                            label="Classe *"
                            @change="changeClasse"
                        />
                    </v-col>
                    <v-col cols="12">                        
                        <v-autocomplete
                            dense outlined
                            :items="entidades"
                            v-model="entitySelected"
                            :item-text="item=>item.Codigo + ' - ' + item.Nome"
                            item-value="ID"
                            label="Entidade"
                            @change="getFuncionariosPorEntidade"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        Pesquisa:
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            dense outlined
                            v-model="searchEntitySelected"
                            label="Nome/Número"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-btn
                            class="success"
                            @click="getFuncionariosPorEntidade"
                        ><v-icon>mdi-magnify</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-text class="mt-4 d-block d-lg-none">
                <v-row no-gutters>
                    <v-col cols="12">                        
                        <v-autocomplete
                            dense outlined
                            :items="entidades"
                            v-model="entitySelected"
                            :item-text="item=>item.Codigo + ' - ' + item.Nome"
                            item-value="ID"
                            label="Entidade"
                            @change="getFuncionariosPorEntidade"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-autocomplete
                            v-if="hideFrentes == false"                                                                            
                            v-model="entitySelectedFrente"
                            :items="frentes_obra"
                            outlined dense
                            class="mt-4"
                            :item-text="ob => getText(ob)"
                            item-value="ID"
                            label="Frentes de Obra *"
                            @change="changeFrenteObraV2Entity"
                        />
                        <span v-else>-</span>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-autocomplete
                            v-model="entitySelectedClasse"
                            :items="entityClasses"
                            class="mt-4"
                            outlined dense
                            :item-text="ob => ob.Descricao"
                            item-value="ID"
                            label="Classe *"
                            @change="changeClasse"
                        />
                    </v-col>
                    <v-col cols="12" md="auto">
                        Pesquisa:
                    </v-col>
                    <v-col cols="12" md="8">
                        <v-text-field
                            dense outlined
                            v-model="searchEntitySelected"
                            label="Nome/Número"
                        />
                    </v-col>
                    <v-col cols="12" md="2" align="center" justify="center">
                        <v-btn
                            class="success"
                            @click="getFuncionariosPorEntidade"
                        ><v-icon>mdi-magnify</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-text v-if="entitySelected != null">
                <!-- DESKTOP -->
                <v-virtual-scroll
                    class="d-none d-lg-block"
                    :height="getWindowHeight" 
                    item-height="75"
                    :items="funcionariosPorEntidade">
                    <template v-slot:default="{ item }">
                        <v-list-item>
                            <v-list-item-content>
                                <v-row>
                                <v-col cols="12" md="8">
                                    <v-text-field 
                                        dense outlined
                                        disabled
                                        v-model="item.funcionario"
                                        label="Funcionario"
                                    />
                                </v-col>
                                <v-col cols="12" md="1">
                                    <v-text-field 
                                        dense outlined
                                        v-model="item.hn"
                                        @keydown="item.selected = true"
                                        label="HN"
                                    />
                                </v-col>
                                <v-col cols="12" md="1">
                                    <v-text-field 
                                        dense outlined
                                        v-model="item.hext"
                                        @keydown="item.selected = true"
                                        label="HExt"
                                    />
                                </v-col>
                                <v-col cols="12" md="1">
                                    <v-text-field 
                                        dense outlined
                                        v-model="item.local"
                                        @keydown="item.selected = true"
                                        label="Nota"
                                    />
                                </v-col>
                                <v-col cols="12" md="1">
                                    <v-switch 
                                        dense
                                        v-model="item.selected"
                                    />
                                </v-col>
                            </v-row>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-virtual-scroll>
                <!-- DESKTOP -->

                <!-- MOBILE -->
                <v-virtual-scroll 
                    v-if="funcionariosPorEntidade.length > 0" 
                    class="d-md-none"
                    height="600" 
                    item-height="475"
                    :items="funcionariosPorEntidade">
                    <template v-slot:default="{ item }">
                        <v-card class="elevation-0" outlined>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-row>
                                    <v-col cols="12" md="8">
                                        <v-text-field 
                                            dense outlined
                                            disabled
                                            v-model="item.funcionario"
                                            label="Funcionario"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="1">
                                        <v-text-field 
                                            dense outlined
                                            v-model="item.hn"
                                            @keydown="item.selected = true"
                                            label="HN"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="1">
                                        <v-text-field 
                                            dense outlined
                                            v-model="item.hext"
                                            @keydown="item.selected = true"
                                            label="HExt"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="1">
                                        <v-text-field 
                                            dense outlined
                                            v-model="item.local"
                                            @keydown="item.selected = true"
                                            label="Nota"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="1">
                                        <v-switch 
                                            dense
                                            v-model="item.selected"
                                        />
                                    </v-col>
                                </v-row>
                                </v-list-item-content>
                            </v-list-item>
                        </v-card>
                    </template>
                    <v-row>
                        <v-col cols="12">
                            <v-data-iterator
                                :items="funcionariosPorEntidade"
                                item-key="id"
                                :items-per-page="-1"
                                hide-default-footer
                                class="d-block d-lg-none ma-0 pa-0"
                            >
                            <template v-slot:default="{ items }">
                                <v-row class="ma-0 pa-0">
                                    <v-col
                                        class="ma-0 pa-0"
                                        v-for="(item, index) in items"
                                        :key="index+900000"
                                        cols="12"
                                    >
                                        <v-card elevation="0" class="mt-2 rounded-lg ma-0 pa-0" outlined>
                                            <v-list
                                                dense
                                            >
                                                <v-list-item>
                                                    <v-list-item-content class="align-center">
                                                        <v-text-field 
                                                            dense outlined
                                                            disabled
                                                            v-model="item.funcionario"
                                                            label="Funcionario"
                                                        />
                                                    </v-list-item-content>                                                                            
                                                </v-list-item>

                                                <v-list-item>
                                                    <v-list-item-content class="align-center">
                                                        <v-text-field 
                                                            dense outlined
                                                            v-model="item.hn"
                                                            @keydown="item.selected = true"
                                                            label="HN"
                                                        />
                                                    </v-list-item-content>                                                                            
                                                </v-list-item>

                                                <v-list-item>
                                                    <v-list-item-content class="align-center">
                                                        <v-text-field 
                                                            dense outlined
                                                            v-model="item.hext"
                                                            @keydown="item.selected = true"
                                                            label="HExt"
                                                        />
                                                    </v-list-item-content>                                                                            
                                                </v-list-item>

                                                <v-list-item>
                                                    <v-list-item-content class="align-center">
                                                        <v-text-field 
                                                            dense outlined
                                                            v-model="item.local"
                                                            @keydown="item.selected = true"
                                                            label="Nota"
                                                        />
                                                    </v-list-item-content>                                                                            
                                                </v-list-item>

                                                <v-list-item>
                                                    <v-list-item-content class="align-center">
                                                        <v-switch 
                                                            v-model="item.selected"
                                                            label="Selecionar"
                                                            inset
                                                        />
                                                    </v-list-item-content>                                                                            
                                                </v-list-item>
                                            </v-list>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </template>
                            </v-data-iterator>
                        </v-col>
                    </v-row>
                </v-virtual-scroll>
                <!-- MOBILE -->
            </v-card-text>
            <v-card-actions v-if="entitySelected != null">
                <v-row>
                    <v-col align="center" justify="center" cols="12">
                        <v-btn class="success" @click="addFuncionariosPorEntidade">Gravar</v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <SuccessSnackbar message="Colaboradores inseridos na diária" v-model="successAddEntity"/>

    <SuccessSnackbar :message="success.message" v-model="success.show"/>

    <v-dialog
        v-if="editorModal"
        v-model="editorModal"
        max-width="600px"
        scrollable
    >
        <InputEditorModal @change-value="changeEditorModal" vid="editor_local" :title="'Nota'" :label="'Nota'" v-model="inputEditorModalData.itemModel"/>
    </v-dialog>
        

    <ErrorSnackbar v-model="error" :message="error_message"/>

    <ErrorSnackbar color="gold" :timeout="6000" v-model="userNaoExisteObra" :message="'[Alguns dados não foram importados] - Ao importar o template o sistema detetou que alguns dados do mesmo não estão associados à obra'"/>

    <ErrorSnackbar color="gold" :timeout="6000" v-model="erros.show" :message="erros.msg"/>

    <Loader v-if="loader"/>

    

    <DialogSuccess :opened="successDialog.show" @on-ok="successDialog.show = false; $router.push('/diarios/v2')" >
        {{successDialog.message}}
    </DialogSuccess>

    <DialogUpdatingDiary v-if="this.fields.locked"/>

    <DialogConfirmation 
      title="A diária pode ser repetida" 
      type="warning" 
      :opened="duplicatedDiary" 
      @on-submit="duplicatedDiary = false" 
      @on-cancel="duplicatedDiary = false; windowReload();">
        Esta diária pode ser duplicada. Pretende continuar? <br/><br/><strong><u>Tenha em consideração</u></strong> que se o fizer, os dados serão somados aos da outra diária!
    </DialogConfirmation>

    <!-- Mensagem de alerta para validar os KPIs -->
    <v-dialog v-model="submitValidDialog.show" max-width="500">
        <v-card>
        <v-card-title class="primary white--text">
            Alerta!
        </v-card-title>

        <v-card-text>
            <div class="mt-2">Encontrámos os problemas que seguem na lista. Se pretender prosseguir mesmo assim, iremos dividir as horas de forma automática entre as unidades.</div>
            <v-list dense>
            <v-list-item v-for="(msg, index) in submitValidDialog.item || []" :key="index">
                <v-list-item-content>
                <v-list-item-title>{{ msg }}</v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            </v-list>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>

            <v-btn color="success" @click="validateKPI_automatic">
            Prosseguir na mesma
            </v-btn>

            <v-btn class="error" @click="submitValidDialog.show = false">
            Cancelar
            </v-btn>
        </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="historico.show" width="50%">
        <v-card>
            <v-card-title class="primary white--text">Histórico de edições</v-card-title>
            <v-card-text>
                <Historico v-if="historico.show" :versions="fields.versions" />
            </v-card-text>
        </v-card>
    </v-dialog>

    <RejectionsHistory
        v-model="rejectionsDialog.show"
        :rejections="fields.rejections"
    />

    <FaultsDialog v-if="faults.show" :fault="faults.data" :value="faults.show" @close="faults.data = null; faults.show = false" @add-fault="addFault"/>

    </v-container>
</template>
<script> 
import PDEnc from "@/api/PDEnc.js" 
import DateInput from "@/components/UI/Inputs/DateInput"
import SuccessSnackbar from "@/components/UI/Snackbars/SuccessSnackbar";
import Obra from "@/api/Obra.js"
import FrenteObra from '@/api/FrenteObra.js'
import Entidades from '@/api/Entidades.js'
import Materiais from '@/api/Materiais.js'
import Classe from "@/api/Classe.js"
import Templates from "@/api/Templates.js"
import dayjs from 'dayjs'
import Equipments from '@/components/Diaries/Supervisor/Objects/Equipments.vue'
import Materials from '@/components/Diaries/Supervisor/Objects/Materials.vue'
import Subcontractors from '@/components/Diaries/Supervisor/Objects/Subcontractors.vue'
import Observations from '@/components/Diaries/Supervisor/Objects/Observations.vue'
import ErrorSnackbar from "@/components/UI/Snackbars/ErrorSnackbar";
import Loader from "@/components/UI/Loaders/Loader";
import InputEditorModal from "@/components/UI/Modals/InputEditorModal";
import Diaries from '@/api/Diaries.js'
import DiaryKPI from '@/components/Diaries/Kpi/DiaryKPI.vue'
import DialogSuccess from '@/components/UI/DialogSuccess.vue';
import DialogConfirmation from '@/components/UI/DialogConfirmation.vue';
import DiaryKPI_API from "@/api/DiaryKPI.js";
import Tooltip from '@/components/UI/Tooltip.vue';
import { readExcelFile } from "@/plugins/excelReader";
import DialogUpdatingDiary from '@/components/UI/DialogUpdatingDiary.vue'
import Historico from '@/components/Diaries/Historico.vue'
import FaultsDialog from '@/components/Faults/Faults.vue'
import Faults from '@/api/Faults.js'
import Work from '@/api/Works.js'
import OrdensTrabalho from '@/api/OrdensTrabalho.js'
import RejectDiary from '@/components/Diaries/Rejections/RejectDiary.vue'
import RejectionsHistory from '@/components/Diaries/Rejections/RejectionsHistory.vue'

export default{
    name:"Supervisor",
    components:{
        FaultsDialog,
        DialogSuccess,
        DialogConfirmation,
        DiaryKPI,
        DateInput,
        Equipments,
        Materials,
        Subcontractors,
        Observations,
        ErrorSnackbar,
        Loader,
        SuccessSnackbar,
        InputEditorModal,
        Tooltip,
        DialogUpdatingDiary,
        Historico,
        RejectDiary,
        RejectionsHistory
    },
    props:{
        method: String,
        diary: null
    },
    watch: {         
        tab(newTab) {
            if (newTab === 4) { // 4 = index da tab "Diárias KPI"
                this.$nextTick(() => {
                    this.$refs.diaria_kpi.reloadMateriais();
                    this.$refs.diaria_kpi.reloadSubempreitadas();
                });
            }
        },
        showContent:{
            immediate: true,
            handler(val){
                if(!val) return

                let data = {
                    data: this.fields.Data,
                    obra_id: this.fields.obra_id,
                    frente_id: this.fields.frente_id
                }

                if(this.method == 'update')
                    return
                Diaries.checkDiaryInDay(data).then((resp)   =>  {
                    if(resp.data.exists) this.duplicatedDiary = true
                })
            }
        },
        entitySelect:{
            immediate: true,
            handler(val){
                if(val == undefined)
                    return

                if(val == false)
                    this.entitySelected = null
            }
        },
        diary: {
            immediate: true, 
            async handler (val) {
                if(!val) {
                    return;
                }
        
                let data = {...val};
        
                this.fields = data;

                if (typeof this.fields.is_rejected === 'undefined' || this.fields.is_rejected === null) {
                    this.$set(this.fields, 'is_rejected', false)
                }

                if (!Array.isArray(this.fields.rejections)) {
                    this.$set(this.fields, 'rejections', [])
                }
               
                this.fields.funcionariosInternos = data.funcionariosInternos == null ? [] : data.funcionariosInternos.linhas
                this.fields.diary_kpi_header_id = data.diary_kpi_header_id
                
                this.fields.funcionariosInternos.forEach(item => {
                    // Inicializa fabOptions para cada item
                    this.$set(this.fabOptions, item.id, false); // false ou true dependendo do seu uso
                });
                this.fields.funcionariosInternos.forEach(element => {
                    element.hn = isNaN(element.hn) ? 0 : parseFloat(element.hn).toFixed(2)
                    element.hn = element.hn % 1 == 0 ? parseInt(element.hn) : element.hn

                    element.hext = isNaN(element.hext) ? 0 : parseFloat(element.hext).toFixed(2)
                    element.hext = element.hext % 1 == 0 ? parseInt(element.hext) : element.hext
                    
                    element.id = element.id + '-' + this.idIncrementor++

                    element.faturar_values = this.valores_faturacao;

                    if(element.fault_type_id != null)
                        element.add_fault = true
                });

                this.fields.funcionariosExternos = data.funcionariosExternos == null ? [] : data.funcionariosExternos.linhas


                

                this.fields.funcionariosExternos.forEach(item => {
                    // Inicializa fabOptions para cada item
                    this.$set(this.fabOptions, item.id, false); // false ou true dependendo do seu uso
                });


                this.fields.funcionariosExternos.forEach(element => {
                    element.hn = isNaN(element.hn) ? 0 : parseFloat(element.hn).toFixed(2)
                    element.hn = element.hn % 1 == 0 ? parseInt(element.hn) : element.hn

                    element.hext = isNaN(element.hext) ? 0 : parseFloat(element.hext).toFixed(2)
                    element.hext = element.hext % 1 == 0 ? parseInt(element.hext) : element.hext

                    element.id = element.id + '-' + this.idIncrementor++

                    element.faturar_values = this.valores_faturacao;
                });

                this.fields.equipamentosInternos = data.equipamentosInternos == null ? [] : data.equipamentosInternos.linhas

                this.fields.equipamentosInternos.forEach(element => {
                    element.hs = isNaN(element.hs) ? 0 : parseFloat(element.hs).toFixed(2)
                    element.hs = element.hs % 1 == 0 ? parseInt(element.hs) : element.hs

                    element.hp = isNaN(element.hp) ? 0 : parseFloat(element.hp).toFixed(2)
                    element.hp = element.hp % 1 == 0 ? parseInt(element.hp) : element.hp

                    element.ho = isNaN(element.ho) ? 0 : parseFloat(element.ho).toFixed(2)
                    element.ho = element.ho % 1 == 0 ? parseInt(element.ho) : element.ho

                    element.ha = isNaN(element.ha) ? 0 : parseFloat(element.ha).toFixed(2)
                    element.ha = element.ha % 1 == 0 ? parseInt(element.ha) : element.ha

                    element.horas = isNaN(element.horas) ? 0 : parseFloat(element.horas).toFixed(2)
                    element.horas = element.horas % 1 == 0 ? parseInt(element.horas) : element.horas

                    element.km = isNaN(element.km) ? 0 : parseFloat(element.km).toFixed(2)
                    element.km = element.km % 1 == 0 ? parseInt(element.km) : element.km

                    element.faturar_values = this.valores_faturacao;
                });

                this.fields.equipamentosExternos = data.equipamentosExternos == null ? [] : data.equipamentosExternos.linhas

                this.fields.equipamentosExternos.forEach(element => {
                    element.hs = isNaN(element.hs) ? 0 : parseFloat(element.hs).toFixed(2)
                    element.hs = element.hs % 1 == 0 ? parseInt(element.hs) : element.hs

                    element.hp = isNaN(element.hp) ? 0 : parseFloat(element.hp).toFixed(2)
                    element.hp = element.hp % 1 == 0 ? parseInt(element.hp) : element.hp

                    element.ho = isNaN(element.ho) ? 0 : parseFloat(element.ho).toFixed(2)
                    element.ho = element.ho % 1 == 0 ? parseInt(element.ho) : element.ho

                    element.ha = isNaN(element.ha) ? 0 : parseFloat(element.ha).toFixed(2)
                    element.ha = element.ha % 1 == 0 ? parseInt(element.ha) : element.ha

                    element.horas = isNaN(element.horas) ? 0 : parseFloat(element.horas).toFixed(2)
                    element.horas = element.horas % 1 == 0 ? parseInt(element.horas) : element.horas

                    element.km = isNaN(element.km) ? 0 : parseFloat(element.km).toFixed(2)
                    element.km = element.km % 1 == 0 ? parseInt(element.km) : element.km

                    element.faturar_values = this.valores_faturacao;
                });
                
                this.fields.materiais = data.materiais

                data.materiais.forEach(element => {
                    element.qty = isNaN(element.qty) ? 0 : parseFloat(element.qty);

                });
                this.fields.subempreitadas = data.subempreitada

                this.fields.deleteMO =[];
                this.fields.deleteEQ =[];
                this.fields.deleteMaterial =[];
                this.fields.deleteSubempreitada =[];

                if(data.newObservationsClasses != null){
                    this.fields.obsMO = data.newObservationsClasses.obs_work_hand
                    this.fields.obsEQ = data.newObservationsClasses.obs_equipments
                }

                
                await this.getObras()

                const w = this.fields.weather == null ? null : JSON.parse(this.fields.weather)
                if(w != null){
                    this.weather = w
                }

                this.changeWork()

                // Diária não tem frente mas tem unidade
                if(this.fields.frente_id == null && this.fields.classe_id != null){
                    this.classesPorObra()
                    this.changeClasse()
                }

                if(this.fields.classe_id != null){
                    this.changeClasse()
                }

            }
        },
        loadUpdateMetadata:{
            immediate: true,
            handler(val){
                if(!val){
                    return;
                }

                this.changeFrenteObra()
            }
        }
    },
    data(){
        return{
            weather: null,
            success:{
                message: null,
                show: false
            },
            faults:{
                data: null,
                show: false
            },
            historico:{
                item: null,
                show: false
            },
            rejectionsDialog:{
                show: false
            },
            submitValidDialog:{
                show: false,
                item: null
            },
            valores_faturacao: ['S', 'N', 'C'],
            diarias_manobrador: [],
            idIncrementor: 0,
            fabOptions: {},
            duplicatedDiary: false,
            successDialog:{
                show: false,
                message: null
            },
            erros:{
                show: false,
                msg: ''
            },
            //Runner counters
            classesPorObraCounter: 0,
            entidadesPorObraCounter: 0,
            getFrenteObraCounter: 0,
            getObrasCounter: 0,
            getFuncionariosCounter: 0,
            // End runner counters
            successAddEntity: false,
            searchEntitySelected: null,
            funcionariosPorEntidade: [],
            entitySelected: null,
            entitySelectedFrente: null,
            entitySelect: false,
            entidades: [],
            menu2: false,
            tab: 0,
            userNaoExisteObra: false,
            hideFrentes: false,
            loadUpdateMetadata: false,
            loader: false,
            loading: true,
            error_message: null,
            linhas: [],
            error: false,
            obras: [],
            frentes_obra: [],
            classes: [],
            funcionarios_externos: [],
            funcionarios_internos: [],
            equipamentos: [],
            template: null,
            showContent: false,
            classeNeeded: false,
            entityClasses: [],
            entityClasseNeeded: false,
            entityHideFrentes: false,
            entitySelectedClasse: null,
            step: 10,
            paginacaoInterna:{
                menor: 0,
                maior: 10
            },
            paginacaoExterna:{
                menor: 0,
                maior: 10
            },
            diary_kpi: null,
            inputEditorModalData:{
                place: null,
                itemModel:null,
                id: null,
                classe_id: null,
                frente_id: null
            },   
            fault_types: [],
            fields:{
                Data: null,
                obra_id: null,
                frente_id: null,
                latest_rejection_reason: null,
                latest_rejection: null,
                classe_id: null,
                funcionariosInternos: [],
                funcionariosExternos: [],
                equipamentosInternos: [],
                equipamentosExternos: [],
                materiais:[],
                subempreitadas: [],
                obsMO: null,
                obsEQ: null,
                deleteMO: [],
                deleteEQ: [],
                deleteMaterial: [],
                deleteSubempreitada: [],
                work_order_obs: [],
                id_ordem_trabalho: null,
                versions: [],
                locked: false,
                is_rejected: false,
                rejections: []
            },
            editorModal:false,
            precisaSincronizarPrecos: false,
            ordens_trabalho: [],
            loadingOrdensTrabalho: false,
        }
    },
    mounted(){         
        this.fillBaseData()    
    },
    methods:{    
        async debugInvalid() {
  const ok = await this.$refs.form.validate();
  console.log("FORM OK?", ok);
  console.log("OBSERVER ERRORS:", this.$refs.form.errors);
  console.log("classeNeeded:", this.classeNeeded, "showNature:", this.showNature);
  console.log("line:", this.line);
},
        clearDataFalta(item){
            item.fault_type_id = null
            item.add_fault = false
        },
        hasPrimaveraIntegrationError(item){
            return item && item.primavera_integration_error != null && String(item.primavera_integration_error).trim() !== ''
        },
        integrationErrorClass(item){
            return this.hasPrimaveraIntegrationError(item) ? 'primavera-integration-error-row' : ''
        },
        onlyNumericOneDot(e) {
            const ctrl = ['Backspace','Delete','ArrowLeft','ArrowRight','Home','End','Tab','Enter'];
            if (ctrl.includes(e.key) || e.ctrlKey || e.metaKey) return;

            const val = e.target.value || '';
            if (/^\d$/.test(e.key)) return;

            if (e.key === '.') {
            if (val.includes('.')) e.preventDefault();
            return;
            }
            e.preventDefault();
        },

        sanitizeNumericOneDot(val, item, field) {
            let s = String(val ?? '');
            s = s.replace(/[^\d.]/g, '');                  // apenas dígitos e ponto
            const i = s.indexOf('.');
            if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, ''); // 1 ponto
            if (s.startsWith('.')) s = '0' + s;            // opcional

            // usar $set para garantir reatividade em arrays/objetos
            this.$set(item, field, (s === '' || s === '.') ? '' : s);

            // (se quiseres guardar como número)
            // this.$set(item, field, (s === '' || s === '.') ? '' : Number(s));
        },
        onDiaryRejected(rejection) {
            this.$set(this.fields, "is_rejected", true)

            if (!Array.isArray(this.fields.rejections)) {
                this.$set(this.fields, "rejections", [])
            }

            if (rejection) {
                this.fields.rejections.unshift(rejection)
            }
        },
        openHistorico(){
            this.historico.show = true
        },
        atualizaSubempreitadas(val){

            // Atualiza o campo com nova referência
            this.fields = {
                ...this.fields,
                subempreitadas: [...val] // <- nova referência!
            };

            if(this.$refs.diary_kpi != undefined)
                this.$refs.diaria_kpi.reloadSubempreitadas();
        },
        getText(ob) {
            if (ob.Descricao && ob.SubEmp && ob.Descricao.includes(ob.SubEmp)) {
                return ob.Descricao;
            }

            return `${ob.SubEmp} - ${ob.Descricao}`;
        },
        getStatusLabelOrdemTrabalho(status) {
            const labels = {
                parada: 'Parada',
                concluida: 'Concluída',
                em_andamento: 'Em andamento'
            }

            return labels[status] || status || '-'
        },
        formatCurrency(value) {
            const number = Number(value || 0)

            return number.toLocaleString('pt-PT', {
                style: 'currency',
                currency: 'EUR'
            })
        },
        getOrdemTrabalhoText(item) {
            if (!item) return ''

            return item.name || ''
        },
        async getOrdensTrabalho() {
            if (this.fields.obra_id == null) {
                this.ordens_trabalho = []
                this.fields.id_ordem_trabalho = null
                return
            }

            this.loadingOrdensTrabalho = true

            const params = {
                obra_id: this.fields.obra_id
            }


            try {
                const resp = await OrdensTrabalho.all(params)

                this.ordens_trabalho = resp.data || []

                if (
                    this.fields.id_ordem_trabalho != null &&
                    !this.ordensTrabalhoDaObra.some(item => String(item.id).toLowerCase() === String(this.fields.id_ordem_trabalho).toLowerCase())
                ) {
                    this.fields.id_ordem_trabalho = null
                }
            } finally {
                this.loadingOrdensTrabalho = false
            }
        },
        validateKPI_automatic(){
            this.$refs.diaria_kpi.fields.divideAllHours = true
            this.$refs.diaria_kpi.fields.divideAllMachines = true
            this.submit()
            this.submitValidDialog.show = false
        },
        updateField(item, campo, valor, tipo) {
            item[campo] = valor;

            if (campo === 'funcionario') {
                this.calculateMaoObraCost(item, tipo);
            }

            if (campo === 'frente_id') {
                this.changeFrenteObraV2(item);
            }

            // Podes meter mais lógica aqui para outros campos se quiseres
        },
        triggerFileInputExternal() {
            this.$refs.fileInputExternal.click();
        },
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        async handleFileUploadExternal(event) {
            const file = event.target.files[0];

            this.loader = true

            try {
                const data = await readExcelFile(file);
                this.processExcelDataExternal(data);
            } catch (error) {
                alert(error.message);
            }
        },
        async handleFileUpload(event) {
            const file = event.target.files[0];

            this.loader = true

            try {
                const data = await readExcelFile(file);
                this.processExcelData(data);
            } catch (error) {
                alert(error.message);
            }
        },
        processExcelData(data) {
            let result = this.$excelValidator(data)
            if(result.success == false){
                alert(result.error);
                return
            }
            

            data = data.slice(1, -2)
            
            let available_classes = []
            
            

            let classeNeeded = false;
            if(this.hideFrentes == true){
                available_classes = this.classes;
                classeNeeded = true;
            }
            let tamanho = 0
            if(this.fields.funcionariosInternos != null)
                tamanho = this.fields.funcionariosInternos.length
            
                
            data.forEach(element => {

                let frente = null
                this.frentes_obra.forEach(frente_obra => {
                    if(frente_obra.Descricao == element[1]){
                        frente = frente_obra.ID
                        available_classes = frente_obra.classes
                        if(available_classes.length > 0) classeNeeded = true
                    }
                });
                
                if (typeof element[2] !== 'string') return;

                let var_classe = element[2] ? element[2].split('-')[0].trim() : null;
                if(var_classe != null)
                    var_classe = available_classes.find(obj => obj.Classe === var_classe);

                if(element[3]){
                    if (this.funcionarios_internos.some(f => f.Codigo === element[3].split('-')[0].trim())) {                        
                        this.fields.funcionariosInternos.push({
                            id: dayjs().unix() + '-' + tamanho + '-new',
                            funcionario: element[3] ? element[3].split('-')[0].trim() : null, // Extrai apenas o número
                            frente_id: frente,
                            classe_id: var_classe == null ? null : var_classe.ID, // Extrai apenas o número
                            classeNeeded: classeNeeded,
                            available_classes: available_classes,
                            hn: parseFloat(element[4]).toFixed(2),
                            hext: parseFloat(element[6]).toFixed(2),
                            local: null,
                            faturar: 'N',
                            faturar_values: ['S', 'N', 'C'],
                            valor_h_normal: parseFloat(element[5]),
                            valor_h_extra: parseFloat(element[7]),
                            options: "",
                            fault_type_id: null,
                            add_fault: false
                        })
                        tamanho = this.fields.funcionariosInternos.length
                    }
                }
            });

            this.loader = false

        },
        processExcelDataExternal(data){
            let result = this.$excelValidator(data)
            if(result.success == false){
                alert(result.error);
                return
            }

            

            data = data.slice(1, -2)
            
            let available_classes = []
            
            let classeNeeded = false;
            if(this.hideFrentes == true){
                available_classes = this.classes;
                classeNeeded = true;
            }
            let tamanho = 0
            if(this.fields.funcionariosExternos != null)
                tamanho = this.fields.funcionariosExternos.length
                
            data.forEach(element => {

                let frente = null
                this.frentes_obra.forEach(frente_obra => {
                    if(frente_obra.Descricao == element[1]){
                        frente = frente_obra.ID
                        available_classes = frente_obra.classes
                        if(available_classes.length > 0) classeNeeded = true
                    }
                });

                if (typeof element[2] !== 'string') return;
                
                let var_classe = element[2] ? element[2].split('-')[0].trim() : null;
                if(var_classe != null)
                    var_classe = available_classes.find(obj => obj.Classe === var_classe);
                
                if(element[3]){
                    if (this.funcionarios_externos.some(f => f.Numero === element[3].split('-')[0].trim())) {                
                        this.fields.funcionariosExternos.push({
                            id: dayjs().unix() + '-' + tamanho + '-new',
                            funcionario: element[3] ? element[3].split('-')[0].trim() : null, // Extrai apenas o número,
                            frente_id: frente,
                            classe_id: var_classe == null ? null : var_classe.ID,
                            funcionariosEntidades: [],
                            classeNeeded: classeNeeded,
                            available_classes: available_classes,
                            hn: parseFloat(element[4]).toFixed(2),
                            hext: parseFloat(element[6]).toFixed(2),
                            local: null,
                            faturar: 'N',
                            faturar_values: ['S', 'N', 'C'],
                            valor_h_normal: parseFloat(element[5]), 
                            valor_h_extra: parseFloat(element[7]),
                            options: ""
                        })
                        tamanho = this.fields.funcionariosExternos.length
                    }
                }
            });

            this.loader = false
        },
        getEspecialidade(place, cod){
            let returner = ''
            if(place == 'interno'){
                this.funcionarios_internos.forEach(element => {
                    if(element.Codigo == cod)
                        returner = element.Especialidade == null ? '' : element.Especialidade.Desig
                });
            }
            if(place == 'externo'){
                this.funcionarios_externos.forEach(element => {
                    if(element.numero == cod)
                        returner = element.Especialidade == null ? '' : element.Especialidade.Desig
                });
            }
            return returner;
        },
        openNativeDatePicker() {
            const input = this.$refs.dateInput.$el.querySelector('input');
            if (input) {
                input.showPicker(); // Força a abertura do seletor de data no iPhone e Android
            }
        },
        async sincronizarPrecos(){
            this.loader = true

            await this.fields.funcionariosInternos.forEach(async item => {
                // Inicializa fabOptions para cada item
                await this.calculateMaoObraCost(item, 'i')
            });

            await this.fields.funcionariosExternos.forEach(async item => {
                // Inicializa fabOptions para cada item
                await this.calculateMaoObraCost(item, 'e')
            });

            await this.fields.equipamentosExternos.forEach(async item => {
                // Inicializa fabOptions para cada item
                await this.calculateEquipmentCost(item, 'e')
            });

            await this.fields.equipamentosInternos.forEach(async item => {
                // Inicializa fabOptions para cada item
                await this.calculateEquipmentCost(item, 'i')
            });


            //alert('Não se esqueça de gravar a diária para os dados ficarem corretos')

            this.loader = false
        },
        verificarCustosLinha(item){
            if(!this.$store.getters.hasRole(['super', 'administrativo', 'engenheiro']))
                return true;

            if(item.valor_h_normal == null && item.valor_h_extra == null){
                this.precisaSincronizarPrecos = true
                return false
            }

            return true
        },
        async calculateMaoObraCost(item, place){
            let data = {
                data: this.fields.Data,
                mo_code: item.funcionario,
                obra_id: this.fields.obra_id
            }

            await Diaries.getPrecoMaoObra(data).then((resp)   =>  {
                if(resp.data.Result == false){
                    item.valor_h_normal = null
                    item.valor_h_extra = null
                    item.faturar = 'N'
                }else{                    
                    item.valor_h_normal = resp.data.PrecoServico
                    item.valor_h_extra = resp.data.PrecoOrdem
                    item.faturar = resp.data.Faturar
                }
                
                item.faturar_values = ['S', 'N', 'C']
            })
        },        
        async calculateEquipmentCost(item, place){
            let data = {
                data: this.fields.Data,
                eq_code: item.equipamento,
                obra_id: this.fields.obra_id
            }

            await Diaries.getPrecoEquipamento(data).then((resp)   =>  {
                if(resp.data.Result == false){
                    item.valor_h_normal = null
                    item.valor_h_extra = null
                    item.faturar = 'N'
                }else{                    
                    item.valor_h_normal = resp.data.PrecoNormal
                    item.valor_h_extra = resp.data.PrecoExtra
                    item.faturar = resp.data.Faturar
                }
                item.faturar_values = ['S', 'N', 'C']
            })
        },
        openEditorModal(item, place){
            this.inputEditorModalData.itemModel = item.local
            this.inputEditorModalData.id = item.id
            this.inputEditorModalData.place = place
            this.inputEditorModalData.classe_id = item.classe_id
            this.inputEditorModalData.frente_id = item.frente_id

            this.editorModal=true
        },
        changeEditorModal(e){
            if(this.inputEditorModalData.place == 'funcionario_interno'){
                this.fields.funcionariosInternos.forEach(element => {
                    if(element.id == this.inputEditorModalData.id && element.classe_id == this.inputEditorModalData.classe_id && element.frente_id == this.inputEditorModalData.frente_id)
                        element.local = e
                });
            }
            if(this.inputEditorModalData.place == 'funcionario_externo'){
                this.fields.funcionariosExternos.forEach(element => {
                    if(element.id == this.inputEditorModalData.id)
                        element.local = e
                });
            }
            if(this.inputEditorModalData.place == 'equipamento_interno'){
                this.fields.equipamentosInternos.forEach(element => {
                    if(element.id == this.inputEditorModalData.id)
                        element.local = e
                });
            }
            if(this.inputEditorModalData.place == 'equipamento_externo'){
                this.fields.equipamentosExternos.forEach(element => {
                    if(element.id == this.inputEditorModalData.id)
                        element.local = e
                });
            }
            if(this.inputEditorModalData.place == 'material'){
                this.fields.materiais.forEach(element => {
                    if(element.id == this.inputEditorModalData.id)
                        element.local = e
                });
            }
            if(this.inputEditorModalData.place == 'subempreitada'){
                this.fields.subempreitadas.forEach(element => {
                    if(element.id == this.inputEditorModalData.id)
                        element.local = e
                });
            }
        },
        showFrente(item){
            if(this.fields.obra_id != null && this.hideFrentes == false)
                return true

            return false
        },
        showClasse(item){
            if(item.classeNeeded)
                return true

            return false
        },
        addFuncionariosPorEntidade(){
                //entity_classes
                // frentes_obra

            let frente = null
            this.frentes_obra.forEach(element => {
                if(element.ID == this.entitySelectedFrente)
                    frente = element.Descricao
            });
            let classe = null
            this.entityClasses.forEach(element => {
                if(element.ID == this.entitySelectedClasse)
                    classe = element.Descricao
            });
            this.funcionariosPorEntidade.forEach(element => {
                if(element.selected){
                    this.fields.funcionariosExternos.push({
                        id: element.id + '-new',
                        funcionario: element.numero,
                        hn: element.hn,
                        frente_id: this.entitySelectedFrente,
                        classe_id: this.entitySelectedClasse,
                        classe_obra: classe == null ? '-' : classe,
                        frente_obra: frente == null ? '-' : frente,
                        hext: element.hext,
                        local: element.local,
                        options: "",
                        faturar: null,
                        faturar_values: ['S', 'N', 'C'],
                        valor_h_normal: null, 
                        valor_h_extra: null,
                        entidade: element.entidade
                    })
                }
            });

            this.getFuncionariosPorEntidade()

            this.entitySelected = null
            this.entitySelect = false
            this.successAddEntity = true
        },
        changedExterno(val){
            let ent = null
            this.funcionarios_externos.forEach(element => {
                if(element.Numero == val){
                    ent = element.entidade
                }
            });

            let pos = -1
            let i = 0
            this.fields.funcionariosExternos.forEach(element => {
                if(element.Numero == val || element.funcionario == val)
                    pos = i

                i++
            });

            if(pos != -1)
                this.fields.funcionariosExternos[pos].entidade = ent
        },
        toEditRow(item){
            if(item.id.includes('new'))
                return true

            return false
        },
        dataChanged(){
            if(this.obras.length == 1){
                this.fields.obra_id = this.obras[0].Id
                this.changeWork()
            }
        },
        formatNumber(val) {
            let num = parseFloat(val);
            return Number.isInteger(num) ? num : parseFloat(num.toFixed(2));
        },
        async importTemplates(){
            this.loader = true
            // Carregar funcionarios internos

            let user_nao_existe_em_obra = false;

           
            this.template.funcionariosInternos.forEach(element => {
                let existe = false

                this.fields.funcionariosInternos.forEach(fInternos => {
                    if(fInternos.funcionario == element.Codigo)
                        existe = true
                });

                if(existe == false){
                    element.Codigo = element.Codigo.replace(/\s/g, '');
                    let external_object = null

                    this.funcionarios_internos.forEach(aux => {
                        if(element.Codigo == aux.Codigo){
                            external_object = aux
                        }
                    });

                    if(external_object != null){
                        let incrementor = this.fields.funcionariosInternos == null ? 0 : this.fields.funcionariosInternos.length

                        this.fields.funcionariosInternos.push({
                            id: dayjs().unix() + '-' + incrementor + '-new-mo-internal-' + this.idIncrementor++,
                            funcionario: element.Codigo,
                            frente_id: element.frente_id,
                            classe_id: element.classe_id,
                            frente_obra: element.frente_obra,
                            classe_obra: element.classe_obra,
                            hn: this.formatNumber(element.hn),
                            hext: this.formatNumber(element.hext),
                            local: element.local,
                            options: '',
                            fault_type_id: null,
                            add_fault: false
                        })
                    }else user_nao_existe_em_obra = true;
                }
            })

            // Carregar funcionarios externos
            this.template.funcionariosExternos.forEach(element => {
                element.numero = element.numero.replace(/\s/g, '');
                

                let external_object = this.funcionarios_externos.find(
                    aux => element.funcionario == aux.ID
                );
                
                if(external_object == null){
                    external_object = this.funcionarios_externos.find(
                        aux => element.numero == aux.numero
                    );
                }   
                

                if (
                    external_object &&
                    !this.fields.funcionariosExternos.some(f => {
                        const fNumero = f.Numero
                            ? String(f.Numero).replace(/\s/g, '')
                            : String(f.funcionario).replace(/\s/g, '');

                        return (
                            fNumero === String(external_object.Numero).replace(/\s/g, '') &&
                            String(f.classe_id) === String(element.classe_id) &&
                            String(f.frente_id) === String(element.frente_id)
                        );
                    })
                ) {
                    this.fields.funcionariosExternos.push({
                        id: `${dayjs().unix()}-${this.fields.funcionariosExternos.length}-new-mo-external-${this.idIncrementor++}`,
                        funcionario: external_object.Numero.replace(/\s/g, ''),
                        frente_id: element.frente_id,
                        classe_id: element.classe_id,
                        frente_obra: element.frente_obra,
                        classe_obra: element.classe_obra,
                        entidade: external_object.entidade,
                        hn: this.formatNumber(element.hn),
                        hext: this.formatNumber(element.hext),
                        local: element.local,
                        options: ''
                    });
                }
            });



            
            // Carregar equipamentos internos
            this.template.equipamentosInternos.forEach(element => {

                let existe = false

                this.fields.equipamentosInternos.forEach(fEquipInternos => {
                    if(fEquipInternos.equipamento == element.equipamento)
                        existe = true
                });

                if(existe == false){
                    let incrementor = this.fields.equipamentosInternos == null ? 0 : this.fields.equipamentosInternos.length

                    this.fields.equipamentosInternos.push({
                        id: dayjs().unix() + '-' + incrementor + '-new-eq-internal-' + this.idIncrementor++,
                        codigo: null,
                        equipamento: element.equipamento,
                        frente_id: element.frente_id,
                        classe_id: element.classe_id,
                        frente_obra: element.frente_obra,
                        classe_obra: element.classe_obra,
                        hs: this.formatNumber(element.hs),
                        ho: this.formatNumber(element.ho),
                        ha: 0,
                        hp: this.formatNumber(element.hp),
                        local: null,
                        options: ""
                    });
                }
            });

            // Carregar equipamentos externos
            this.template.equipamentosExternos.forEach(element => {
                let existe = false

                this.fields.equipamentosExternos.forEach(fEquipExternos => {
                    if(fEquipExternos.equipamento == element.equipamento)
                        existe = true
                });

                if(existe == false){
                    let incrementor = this.fields.equipamentosExternos == null ? 0 : this.fields.equipamentosExternos.length

                    this.fields.equipamentosExternos.push({
                        id: dayjs().unix() + '-' + incrementor + '-new-eq-external-' + this.idIncrementor++,
                        codigo: null,
                        equipamento: element.equipamento,
                        frente_id: element.frente_id,
                        classe_id: element.classe_id,
                        frente_obra: element.frente_obra,
                        classe_obra: element.classe_obra,
                        hs: this.formatNumber(element.hs),
                        ho: this.formatNumber(element.ho),
                        ha: 0,
                        hp: this.formatNumber(element.hp),
                        local: null,
                        options: ""
                    });
                }
            });


            let materiais_list = []
            let materialsData ={
                id_frente_obra: this.fields.frente_id,
                id_obra_classe: this.fields.classe_id == null ? null : '1',
                obra_id: null
            }
            await Materiais.getList(materialsData).then((resp)    =>  {
                materiais_list = resp.data
            })

            this.template.materiais.forEach(element => {
                let material_object = null
                materiais_list.forEach(aux => {
                    if(aux.artigo == element.artigo)
                        material_object = aux
                });
                let incrementor = this.fields.materiais == null ? 0 : this.fields.materiais.length

                this.fields.materiais.push({
                    id: dayjs().unix() + '-' + incrementor + '-new-' + this.idIncrementor++,
                    material: material_object,
                    frente_id: element.frente_id,
                    classe_id: element.classe_id,
                    frente_obra: element.frente_obra,
                    classe_obra: element.classe_obra,
                    un: "-",
                    qty: 0,
                    local: null,
                    Unidade: '-',
                    options: ""
                });
            });

            if(user_nao_existe_em_obra){
                this.userNaoExisteObra = false
            }

            // As linhas importadas ja trazem frente_id e classe_id,
            // mas precisam das opcoes para o v-autocomplete mostrar a classe.
            // Reutiliza a rotina do update, preservando os IDs do template.
            try {
                await this.loadExistingRowClasses(true)
            } catch (error) {
                console.error('Erro ao carregar classes do template:', error)
            }

            this.loader = false
        },
        async changeClasse(){
            this.loading = true
            let object_data = {
                id: this.fields.obra_id,
                frente: this.fields.frente_id,
                unidade: this.fields.classe_id,
                short_id: false,
                obra_id: this.fields.obra_id
            }
            
            /*Obra.getObrasByClassFuncionarios(object_data).then((resp)  =>  {
                this.funcionarios_externos = resp.data.externo
                this.funcionarios_internos = resp.data.interno
            })*/

            this.loadTemplates()

            await this.getOrdensTrabalho()

            this.showContent = true
            this.loading = false
        },
        async changeFrenteObra(){
            this.loading = true

            let frente = null
            this.frentes_obra.forEach(element => {
                if(element.ID == this.fields.frente_id)
                    frente = element
            });

            if(frente != null){
                const obra = this.obras.find(o => o.Id == this.fields.obra_id)
                let data = {
                    SubEmp: frente.SubEmp,
                    obra: obra ? obra.Codigo : null
                }

                await Classe.getClasses(data).then((resp) =>  {
                    this.classes = resp.data

                    if(this.$store.getters.hasRole(['parque']))
                        this.tab = 1


                    if(this.classes.length == 1){
                        this.fields.classe_id = this.classes[0].ID
                        this.changeClasse()
                    }
                })

                if(this.classes.length == 0){
                    this.showContent = true
                    this.classeNeeded = false
                }else this.classeNeeded = true
            }

            this.loadTemplates()

            await this.getOrdensTrabalho()
            
            this.loading = false
        },
        async changeFrenteObraV2(item){
            this.loading = true

            let frente = null
            this.frentes_obra.forEach(element => {
                if(element.ID == item.frente_id)
                    frente = element
            });


            let entrei = false
            if(frente != null){
                if(frente.classes != null){
                    if(frente.classes.length > 0){
                        entrei = true
                        item.available_classes = frente.classes
                        item.classeNeeded = true
                    }
                }

                if(entrei == false){
                    const obra = this.obras.find(o => o.Id == this.fields.obra_id)
                    let data = {
                        SubEmp: frente.SubEmp,
                        obra: obra ? obra.Codigo : null
                    }


                    await Classe.getClasses(data).then((resp) =>  {
                        item.available_classes = resp.data

                        if(item.available_classes.length == 0){
                            item.classeNeeded = false
                        }else item.classeNeeded = true
                        
                    })
                }
                
            }

           
            
            this.loading = false
        },
        async changeFrenteObraV2Entity(){
            this.loading = true

            let frente = null
            this.frentes_obra.forEach(element => {
                if(element.ID == this.entitySelectedFrente)
                    frente = element
            });

            if(frente != null){
                const obra = this.obras.find(o => o.Id == this.fields.obra_id)
                let data = {
                    SubEmp: frente.SubEmp,
                    obra: obra ? obra.Codigo : null
                }

                await Classe.getClasses(data).then((resp) =>  {
                    this.entityClasses = resp.data
                })

                if(this.entityClasses.length == 0){
                    this.entityClasseNeeded = false
                }else this.entityClasseNeeded = true
            }

            this.loadTemplates()
            
            this.loading = false
        },
        async changeWork(){
            this.loading = true

            this.fields.id_ordem_trabalho = null
            this.ordens_trabalho = []

            let dt = {
                id: this.fields.obra_id,
                date: this.fields.Data
            }

            if(this.weather == null){
                if(this.method == 'create'){
                    Work.getWeather(dt).then((resp) =>  {
                        this.weather = resp.data
                    })
                }
            }

            let data = {
                work_id: this.fields.obra_id,
                codigo: this.$store.state.user.data.funcionario.Codigo,
                place: 'encarregado'
            }
            
            this.getFrenteObra();

            await this.getFuncionarios();

            this.loadTemplates()

            this.entidadesPorObra()

            await this.getOrdensTrabalho()

            //await this.loadManobradores();

            this.loading = false

            this.showContent = true
        },
        async loadManobradores(){
            this.loading = true
            let data = {
                obra_id: this.fields.obra_id,
                data: this.fields.Data
            }

            await Diaries.getDiariasManobradorEncarregado(data).then((resp)   =>  {
                this.diarias_manobrador = resp.data
                this.loading = false

                if(this.diarias_manobrador.length == 0){
                    this.error_message = "Não existem diárias de manobrador para mostrar!"
                    this.error = true
                }else{
                    this.success.message = "Manobradores carregados com sucesso!"
                    this.success.show = true
                }
            })
        },
        async loadExistingRowClasses(force = false){
            if(this.method !== 'update' && !force) return

            const linhas = [
                ...(this.fields.funcionariosInternos || []),
                ...(this.fields.funcionariosExternos || []),
                ...(this.fields.equipamentosInternos || []),
                ...(this.fields.equipamentosExternos || []),
                ...(this.fields.materiais || [])
            ]

            if(this.hideFrentes){
                linhas.forEach(item => {
                    this.$set(item, 'available_classes', this.classes)
                    this.$set(item, 'classeNeeded', this.classes.length > 0 || item.classe_id != null)
                })
                return
            }

            const obra = this.obras.find(o => o.Id == this.fields.obra_id)
            const porFrente = new Map()

            linhas.forEach(item => {
                if(item.frente_id == null) return
                const frente = this.frentes_obra.find(f => f.ID == item.frente_id)
                if(!frente) return
                if(!porFrente.has(frente.ID)) porFrente.set(frente.ID, frente)
            })

            await Promise.all(Array.from(porFrente.values()).map(async frente => {
                try {
                    let classes = Array.isArray(frente.classes) ? frente.classes : []
                    if(classes.length === 0){
                        const resp = await Classe.getClasses({
                            SubEmp: frente.SubEmp,
                            obra: obra ? obra.Codigo : null
                        })
                        classes = Array.isArray(resp.data) ? resp.data : []
                    }

                    this.$set(frente, 'classes', classes)
                    linhas.forEach(item => {
                        if(item.frente_id != frente.ID) return
                        this.$set(item, 'available_classes', classes)
                        this.$set(item, 'classeNeeded', classes.length > 0 || item.classe_id != null)
                    })
                } catch(error){
                    console.error('Erro a carregar classes da frente:', frente.SubEmp, error)
                }
            }))
        },
        getFrenteObra(){
            if(this.getFrenteObraCounter > 0)
                return

            this.getFrenteObraCounter++

            let ObraPai = null
            // Vou obter o ObraPai que é o código da obra, para obter a sua frente
            // Códigos exemplo: 0110203 é da obra MOLHE SINES 3ª F
            // Vamos à tabela ObraFrente ver quais as obras que têm esse código
            this.obras.forEach(element => {
                if(element.Id == this.fields.obra_id)
                    ObraPai = element.Codigo
            });

            FrenteObra.all(ObraPai).then(async (resp) =>  {
                this.frentes_obra = resp.data


                if(this.method=='update')
                    this.loadUpdateMetadata = true

                if(this.frentes_obra.length == 0)
                    this.classesPorObra()

                if(this.frentes_obra.length == 1){
                    this.fields.frente_id = this.frentes_obra[0].ID
                    this.changeFrenteObra()
                }

                // No update, carregar as classes de cada linha sem obrigar
                // o utilizador a mudar manualmente a frente de obra.
                await this.loadExistingRowClasses()
            })   
        },
        async getFuncionarios(){
            if(this.getFuncionariosCounter > 0)
                return

            this.getFuncionariosCounter++
            let data = {
                id: this.fields.obra_id,
                frente: null
            }

            await Obra.getObraFuncionarios(data).then((resp)  =>  {
                this.funcionarios_internos = resp.data
            })

            await Obra.getObraPessoal(this.fields.obra_id).then((resp)    =>  {
                this.funcionarios_externos = resp.data
            })
        },
        entidadesPorObra(){
            if(this.entidadesPorObraCounter > 0)
                return 

            this.entidadesPorObraCounter++
            Entidades.byWork(this.fields.obra_id).then((resp)   =>  {
                this.entidades = resp.data.entidades
            })
        },
        classesPorObra(){
            if(this.classesPorObraCounter > 0)
                return;

            this.classesPorObraCounter++
            // Também temos de verificar se esta obra tem classes
            Classe.getClassesByObraPai(this.fields.obra_id).then((resp) =>  {
                this.classes = resp.data
                this.entityClasses = resp.data
                this.hideFrentes = true
                this.classeNeeded = true

                if(this.$store.getters.hasRole(['parque']))
                    this.tab = 1


                if(this.classes.length == 1){
                    this.fields.classe_id = this.classes[0].ID
                    this.changeClasse()
                }
               
            })
        },
        loadTemplates(){
            if(this.method == 'update')
                return
                
            let frente = null
            
            this.frentes_obra.forEach(element => {
                if(element.ID == this.fields.frente_id)
                    frente = element
            });

            let classe = null
            this.classes.forEach(element => {
                if(element.ID == this.fields.classe_id)
                    classe = element
            });

            // Ver se há templates
            let codesData = {
                classeId: classe == null ? null : classe.ClasseId,
                subEmpId: frente == null ? null : frente.SubEmpId,
                work_code: this.fields.obra_id
            }
            
            Templates.getByCodes(codesData).then(res => {
                if(res.data.template != null)
                    this.template =  res.data.template
            })
        },
        async getObras(){
            if(this.getObrasCounter > 0)
                return

            this.getObrasCounter++
            let data = {
                tipo: 'O'
            }
            
            await Obra.all(data).then((resp)   =>  {
                this.obras = resp.data;
                
                if(this.method == 'update')
                    this.changeWork()   

            });
        },
        async fillBaseData(){
            await this.getObras();

            // Obter diary de KPI se existir
            if(this.method == 'update'){
                if(this.fields.diary_kpi_header_id != null){
                    DiaryKPI_API.find(this.fields.diary_kpi_header_id).then(({data}) => { 
                        this.diary_kpi = data; 

                        console.log("A KPI")
                        console.log(data)
                    });
                }
            }

            Faults.getTypes().then((resp)   =>  {
                this.fault_types = resp.data
            })
                    
                
            this.loading = false
        },        
        addFault(item){
            console.log("FALTA")
            console.log(item)
            console.log(this.faults.data)

            const el = this.fields.funcionariosInternos.find(e =>
                e.id === this.faults.data.id &&
                e.funcionario === this.faults.data.funcionario
            )

            if (el) {
                el.fault_type_id = item
                el.hn = 0
                el.hext = 0
            }          
        },
        marcarFaltaDialog(item){
            console.log("Marcação de falta")
            console.log(item)
            let data = {
                CodigoMO: item.funcionario,
                DataMov: this.fields.Data,
                day: this.fields.Data,
                fault_type_id: item.fault_type_id,
                funcionario: item.funcionario,
                work_id: this.fields.obra_id,
                id: item.id
            }

            this.faults.data = data
            this.faults.show = true
        },
        deleteFuncionarioInterno(id){
            let pos = -1    
            let i = 0
            this.fields.funcionariosInternos.forEach(element => {
                if(element.id == id){
                    this.fields.deleteMO.push(element.primary_id)
                    pos = i
                }

                i++
            });

            //this.fields.deleteMO.push(id)

            this.fields.funcionariosInternos.splice(pos, 1)
        },
        deleteFuncionarioExterno(id){
            let pos = -1    
            let i = 0
            this.fields.funcionariosExternos.forEach(element => {
                if(element.id == id){
                    this.fields.deleteMO.push(element.primary_id)
                    pos = i
                }

                i++
            });

            this.fields.funcionariosExternos.splice(pos, 1)
        },
        addFuncionarioInterno() {
            let available_classes = []
            let classeNeeded = false;
            if(this.hideFrentes == true){
                available_classes = this.classes;
                classeNeeded = true;
            }
            let tamanho = 0
            if(this.fields.funcionariosInternos != null)
                tamanho = this.fields.funcionariosInternos.length

            this.fields.funcionariosInternos.push({
                id: dayjs().unix() + '-' + tamanho + '-new',
                funcionario: null,
                frente_id: null,
                classe_id: null,
                classeNeeded: classeNeeded,
                available_classes: available_classes,
                hn: parseFloat(8),
                hext: 0, //parseFloat(0),
                local: null,
                faturar: null,
                faturar_values: ['S', 'N', 'C'],
                valor_h_normal: null, 
                valor_h_extra: null,
                options: "",
                fault_type_id: null,
                add_fault: false
            })

            // 👇 Atualiza a página automaticamente após adicionar
            const total = this.fields.funcionariosInternos.length;
            const novaPagina = Math.floor((total - 1) / this.step); // começa em 0

            this.paginacaoInterna.menor = novaPagina * this.step;
            this.paginacaoInterna.maior = this.paginacaoInterna.menor + this.step;
        },
        addFuncionarioExterno() {
            let available_classes = []
            let classeNeeded = false;
            if(this.hideFrentes == true){
                available_classes = this.classes;
                classeNeeded = true;
            }

            let tamanho = 0
            if(this.fields.funcionariosExternos != null)
                tamanho = this.fields.funcionariosExternos.length
            
            this.fields.funcionariosExternos.push({
                id: dayjs().unix() + '-' + tamanho + '-new',
                funcionario: null,
                frente_id: null,
                classe_id: null,
                funcionariosEntidades: [],
                classeNeeded: classeNeeded,
                available_classes: available_classes,
                hn: parseFloat(8).toFixed(2),
                hext: parseFloat(0).toFixed(2),
                local: null,
                faturar: null,
                faturar_values: ['S', 'N', 'C'],
                valor_h_normal: null, 
                valor_h_extra: null,
                options: ""
            })

            const total = this.fields.funcionariosExternos.length;
            const novaPagina = Math.floor((total - 1) / this.step);

            this.paginacaoExterna.menor = novaPagina * this.step;
            this.paginacaoExterna.maior = this.paginacaoExterna.menor + this.step;
        },
        async submit(){            
            this.loader = true;



            let obra = null
            // Vou obter o ObraPai que é o código da obra, para obter a sua frente
            // Códigos exemplo: 0110203 é da obra MOLHE SINES 3ª F
            // Vamos à tabela ObraFrente ver quais as obras que têm esse código
            this.obras.forEach(element => {
                if(element.Id == this.fields.obra_id)
                    obra = element
            });



            let frente = null            
            this.frentes_obra.forEach(element => {
                if(element.ID == this.fields.frente_id)
                    frente = element
            });

            let classe = null
            this.classes.forEach(element => {
                if(element.ID == this.fields.classe_id)
                    classe = element
            });

            
            let data = {
                classeID: classe == null ? null : classe.ClasseId,
                id_user_creator: this.fields.id_user,
                SubEmpId: frente == null ? null : frente.SubEmpId,
                Codigo: obra.Codigo,
                classe: classe == null ? null : classe.ClasseId,    
                Data: this.fields.Data,
                maoObraInterna: this.fields.funcionariosInternos,
                maoObraExterna: this.fields.funcionariosExternos,
                equipamentoInterno: this.fields.equipamentosInternos,
                equipamentoExterno: this.fields.equipamentosExternos,
                material: this.fields.materiais,
                subEmpreitadas: this.fields.subempreitadas,
                obsMO: this.fields.obsMO,
                obsEQ: this.fields.obsEQ,
                hash: this.$route.params.hash,
                work_order_obs: this.fields.work_order_obs,
                id_ordem_trabalho: this.fields.id_ordem_trabalho,
                toDelete:{
                    material: this.fields.deleteMaterial,
                    equipment: this.fields.deleteEQ,
                    MO: this.fields.deleteMO,
                    subEmpreitadas: this.fields.deleteSubempreitada
                }
            };
            

            // Verificar se há 1 linha de mao de obra externa/interna
            let arrays_vazios = 0
            if(this.fields.funcionariosInternos.length == 0)
                arrays_vazios++
            if(this.fields.funcionariosExternos.length == 0)
                arrays_vazios++
            if(this.fields.equipamentosInternos.length == 0)
                arrays_vazios++
            if(this.fields.equipamentosExternos.length == 0)
                arrays_vazios++

            if(arrays_vazios == 4){
                this.error_message = "Adicione pelo menos 1 equipamento ou mão de obra"
                this.error = true
                this.loading = false;
                this.loader = false;
                return
            }

            // Não podem existir horas de trabalho a zero
            // Vamos a todos os funcionarios
            // Internos e externos
            // Verificar as horas que eles têm registadas (Ho + Hext > 0)
            /*let existem_horas_a_zero = false;
            this.fields.funcionariosInternos.forEach(element => {
                let res = parseFloat(element.hn) + parseFloat(element.hext)
                if(res <= 0)
                    existem_horas_a_zero = true
            });

            this.fields.funcionariosExternos.forEach(element => {
                let res = parseFloat(element.hn) + parseFloat(element.hext)
                if(res <= 0)
                    existem_horas_a_zero = true
            });

            if(existem_horas_a_zero){
                this.error_message = 'Os funcionários não podem ter valores de trabalho a zero (Hn + Hext > 0)'
                this.error = true
                this.loader = false
                return
            }

            this.fields.equipamentosInternos.forEach(element => {
                let res = parseFloat(element.hs) + parseFloat(element.ho) + parseFloat(element.ha) + parseFloat(element.hp)
                if(res <= 0)
                    existem_horas_a_zero = true
            });

            this.fields.equipamentosExternos.forEach(element => {
                let res = parseFloat(element.hs) + parseFloat(element.ho) + parseFloat(element.ha) + parseFloat(element.hp)
                if(res <= 0)
                    existem_horas_a_zero = true
            });

            if(existem_horas_a_zero){
                this.error_message = 'Os equipamentos não podem ter valores de trabalho a zero (Hs + Ho + Ha + Hp > 0)'
                this.error = true
                this.loader = false
                return
            }*/

            // Horas tem de estar entre 0 e 40
            let horas_checked = true
            let horas_preenchidas = true
            this.fields.funcionariosInternos.forEach(element => {
            console.log(element)
                if(element.hn < 0 || element.hn > 40){
                    horas_checked = false   
                }
                    
                if (element?.hn === '' || element?.hn == null) { // == null cobre null e undefined
                    horas_checked = false;
                }

                if(element.hext < 0 || element.hext > 40){
                    horas_checked = false       
                }
                    
                if(element.hext == null){
                    horas_preenchidas = false
                }
                

            });

            if(horas_checked == false){
                this.error_message = 'As horas dos funcionários internos devem estar compreendidas entre 0h e 40h'
                this.error = true
                this.loader = false
                return
            }

            if(horas_preenchidas == false){
                this.error_message = 'Existe Mão Obra Interna sem Horas Extra definidas'
                this.error = true
                this.loader = false
                return
            }
            this.fields.funcionariosExternos.forEach(element => {
            console.log(element)
                if(element.hn < 0 || element.hn > 40)
                    horas_checked = false    

                if(element.hn === '' || element.hn == null)
                    horas_checked = false       

                if(element.hext === '' || element.hext == null)
                    horas_checked = false              
            });
            if(horas_checked == false){
                this.error_message = 'As horas dos FUNCIONÁRIOS externos devem estar compreendidas entre 0h e 40h'
                this.error = true
                this.loader = false
                return
            }


            let invalid_entities = []
            this.fields.equipamentosInternos.forEach(element => {
                if (element.categoria === 'MPE') {

                    let ho = element.ho
                    let hs = element.hs
                    let ha = element.ha
                    let hp = element.hp

                    if (
                        Number(ho) === 0 &&
                        Number(hs) === 0 &&
                        Number(ha) === 0 &&
                        Number(hp) === 0
                    ) {
                        horas_checked = false
                        invalid_entities.push(element.entity_key)
                    }
                }
            })

            if (!horas_checked) {

                let lista = invalid_entities
                    .map(k => `- ${k}`)
                    .join('\n')

                this.error_message =
                    'As horas dos equipamentos pesados não podem ser todas a zero (Hs, Ho, Ha, Hp)\n\n' +
                    'Categoria MPE:\n' +
                    lista

                this.error = true
                this.loader = false
                return
            }

            /*this.fields.equipamentosInternos.forEach(element => {
                if(element.hs < 0 || element.hs > 40)
                    horas_checked = false  
                if(element.ho < 0 || element.ho > 40)
                    horas_checked = false  
                if(element.ha < 0 || element.ha > 40)
                    horas_checked = false  
                if(element.hp < 0 || element.hp > 40)
                    horas_checked = false  
            });
            if(horas_checked == false){
                this.error_message = 'As horas dos EQUIPAMENTOS internos devem estar compreendidas entre 0h e 40h'
                this.error = true
                this.loader = false
                return
            }

            this.fields.equipamentosExternos.forEach(element => {
                if(element.hs < 0 || element.hs > 40)
                    horas_checked = false  
                if(element.ho < 0 || element.ho > 40)
                    horas_checked = false  
                if(element.ha < 0 || element.ha > 40)
                    horas_checked = false  
                if(element.hp < 0 || element.hp > 40)
                    horas_checked = false  
            });
            if(horas_checked == false){
                this.error_message = 'As horas dos EQUIPAMENTOS externos devem estar compreendidas entre 0h e 40h'
                this.error = true
                this.loader = false
                return
            }*/
   
            
            data.kpi_diary = null;
            if(this.$refs.diaria_kpi != undefined){
                let valid = await this.$refs.diaria_kpi.linhasValidas();

                if(valid.valid == false){
                    this.loader = false
                    let message = []
                    valid.message.forEach(element => {
                        message.push(element.classe.Descricao + ' na natureza ' + element.natureza.natureza)
                    });


                    this.submitValidDialog.item = message
                    this.submitValidDialog.show = true
                    return
                }

                data.kpi_diary = await this.$refs.diaria_kpi.getData(this.$route.params.hash, this.fields.id_user);
            }

     
            PDEnc[this.method](data, data.hash).then(async (resp) => {
                this.success = true;

                this.error = false;

                this.loader = false;

                let link = resp.data
                
                /*if(this.$refs.diaria_kpi != undefined)
                    await this.$refs.diaria_kpi.submit(link.hash, link.creator)*/

                if(this.method == 'create'){
                    this.successDialog.message = "A sua diária foi enviado para o sistema e irá ser processada assim que possível. Iremos reencaminhá-lo para a lista das diárias. Brevemente terá disponível a diária que criou agora."
                    this.successDialog.show = true
                    //this.$router.push("/diarios/encarregado/editar/" + link.hash + "/" + link.creator);
                }else{
                    this.successDialog.message = "Diária enviada para atualização. Brevemente a informação será atualizada!"
                    this.successDialog.show = true
                    //window.location.reload()
                }

            }).catch(err => {
                console.log(err)

                if(err.response.status == 422){

                    this.$refs.pd.$refs.form.setErrors(err.response.data.errors);

                }
                
                this.success = false;

                this.error = true;

                this.error_message = err.response.data.message;

                this.loader = false;
            });
        },
        windowReload(){
            window.location.reload()
        },
        getFuncionariosPorEntidade(){
            let r = []

            this.funcionariosPorEntidade = []

            this.funcionarios_externos.forEach(element => {
                if(element.entidade != null){
                    if(element.entidade.ID == this.entitySelected){
                        let func = element.Numero + ' - ' + element.Nome
                        
                        let ob = null;
                        if(this.searchEntitySelected == null){
                            ob = {
                                    id: element.ID,
                                    funcionario: func,
                                    hn: 8,
                                    hext: 0, 
                                    local: null,
                                    selected: false,
                                    numero: element.Numero,
                                    entidade: element.entidade
                                }
                        }else{
                            if(func.toLowerCase().includes(this.searchEntitySelected.toLowerCase())){                                
                                ob = {
                                        id: element.ID,
                                        funcionario: func,
                                        hn: 8,
                                        hext: 0, 
                                        local: null,
                                        selected: false,
                                        numero: element.Numero,
                                        entidade: element.entidade
                                    }
                            }
                        }


                        let existe = false
                        if(ob != null){
                            this.fields.funcionariosExternos.forEach(aux => {
                                if(aux.funcionario == ob.numero)
                                    existe = true
                            });
                        }

                        if(!existe)
                            if(ob != null)
                                r.push(ob)
                    }
                }
            });


            this.funcionariosPorEntidade = r
        },
        validateDate(value) {
            if (!value) return "A data é obrigatória";
            if (value > this.maxDate) return "A data não pode ser futura";
            return true;
        },     
        getFuncionariosInternosDisponiveis(currentFuncionario = null){
            let a = true

            if(a) return this.funcionarios_internos
            
            let array_funcionarios = [];
            let array_used = [];

            this.fields.funcionariosInternos.forEach(element => {
                if (element.funcionario && element.funcionario !== currentFuncionario) {
                    array_used.push(element.funcionario);
                }
            });

            this.funcionarios_internos.forEach((funcionario_available) => {
                const used = array_used.includes(funcionario_available.Codigo);
                if (!used || funcionario_available.Codigo === currentFuncionario) {
                    array_funcionarios.push(funcionario_available);
                }
            });

            return array_funcionarios;
        }
    },
    computed:{         
        latestRejection() {
            if (!Array.isArray(this.fields.rejections)) {
                return null
            }

            if (this.fields.rejections.length === 0) {
                return null
            }

            return this.fields.rejections[0]
        },
        ordensTrabalhoDaObra() {
            if (!this.fields.obra_id) {
                return []
            }

            return this.ordens_trabalho.filter(item => {
                const obraId = item.obra_id || item.ObraId || item.obra?.Id || item.obra?.id

                return String(obraId).toLowerCase() === String(this.fields.obra_id).toLowerCase()
            })
        },

        addInternoDisabled () {
            const linhasBackend = this.fields.funcionariosInternos || [] // <-- mete aqui o array certo

            const existeClasseSemFrente = linhasBackend.some(l =>
                l.frente_id == null && l.classe_id != null
            )

            if (existeClasseSemFrente) {
                return this.classes.length === 0
            }

            return false
        },
        weatherSummary () {
            // nada carregado ainda
            if (!this.weather) return null

            let meta = null

            try {
            // CASO 1: no update já tens o objeto completo (com day lá dentro)
            if (this.method === 'update') {
                if (this.weather.day) {
                meta = this.weather
                } else if (this.weather.metadata) {
                // se por acaso vier num wrapper com metadata
                meta = typeof this.weather.metadata === 'string'
                    ? JSON.parse(this.weather.metadata)
                    : this.weather.metadata
                }
            } else {
                // CASO 2: create – vem sempre em this.weather.metadata
                if (!this.weather.metadata) return null

                meta = typeof this.weather.metadata === 'string'
                ? JSON.parse(this.weather.metadata)
                : this.weather.metadata
            }
            } catch (e) {
            console.error('Erro ao fazer parse do metadata de weather:', e)
            return null
            }

            if (!meta || !meta.day) return null

            const day = meta.day
            const conditionText = day.condition?.text || ''

            const lower = conditionText.toLowerCase()
            let icon = 'mdi-weather-partly-cloudy'

            if (lower.includes('chuva') || lower.includes('chuvisco') || lower.includes('aguace')) {
                icon = 'mdi-weather-rainy'
            } else if (lower.includes('neve')) {
                icon = 'mdi-weather-snowy'
            } else if (lower.includes('neblina') || lower.includes('nevoeiro') || lower.includes('nevoa')) {
                icon = 'mdi-weather-fog'
            } else if (lower.includes('sol') || lower.includes('céu limpo') || lower.includes('ceu limpo')) {
                icon = 'mdi-weather-sunny'
            } else if (lower.includes('nublado') || lower.includes('encoberto')) {
                icon = 'mdi-weather-cloudy'
            }

            const dt = {
                icon,
                conditionText,
                max: day.maxtemp_c,
                min: day.mintemp_c,
                wind: day.maxwind_kph,
            }

            // debug extra, só para confirmares:
            console.log('weatherSummary ->', dt)

            return dt
        },
        isMobile() {
            return this.$vuetify.breakpoint.smAndDown;
        },
        especialidadeMapGeral() {
            const map = {};

            // Internos
            this.fields.funcionariosInternos.forEach(item => {
            if (item.funcionario && !map['interno_' + item.funcionario]) {
                map['interno_' + item.funcionario] = this.getEspecialidade('interno', item.funcionario);
            }
            });

            // Externos
            this.fields.funcionariosExternos.forEach(item => {
            if (item.funcionario && !map['externo_' + item.funcionario]) {
                map['externo_' + item.funcionario] = this.getEspecialidade('externo', item.funcionario);
            }
            });

            return map;
        },
        totalFuncionariosExternos() {
            return this.fields.funcionariosExternos.length
        },
        totalPagesExterna() {
            const total = Math.ceil(this.totalFuncionariosExternos / this.step)
            return total > 0 ? total : 1
        },
        currentPageExterna() {
            return Math.floor(this.paginacaoExterna.menor / this.step) + 1
        },
        totalFuncionariosInternos() {
            let total = 0

            this.diarias_manobrador.forEach(diaria => {
                total += diaria.parte_diaria.linhas_mo.length
            })

            total += this.fields.funcionariosInternos.length

            return total 
        },
        totalPagesInterna() {
            const total = Math.ceil(this.totalFuncionariosInternos / this.step)
            return total > 0 ? total : 1
        },
        currentPageInterna() {
            return Math.floor(this.paginacaoInterna.menor / this.step) + 1
        },
        maxDate() {
            return new Date().toISOString().split("T")[0]; // Define a data máxima como hoje (YYYY-MM-DD)
        },
        approvedDiary(){
            if(this.method == 'create')
                return false;
            return this.fields.approved
        },
        computeFuncionariosExternos() {
            let returner = []

            this.fields.funcionariosExternos.forEach(element => {
                if (element.classe_id != null) {
                    if (!this.hideFrentes) {
                        this.frentes_obra.forEach(f => {
                            if (element.frente_id == f.ID) {
                                if (Array.isArray(f.classes) && f.classes.length > 0) {
                                    element.available_classes = f.classes
                                }
                            }
                        });
                    } else {
                        element.available_classes = this.classes
                    }
                    element.classeNeeded = true
                }
                returner.push(element)
            });

            let start = this.paginacaoExterna.menor
            let end = start + this.step
            return returner.slice(start, end)
        },
        computeFuncionariosInternos() {
            let returner = []

            this.diarias_manobrador.forEach(diaria => {
                diaria.parte_diaria.linhas_mo.forEach(element => {
                    returner.push({
                        id: dayjs().unix() + '-' + element.creator + '-new-non-editable',                    
                        funcionario: element.CodigoMO,
                        frente_id: diaria.frente_id,
                        classe_id: diaria.classe_id,
                        classeNeeded: diaria.classe_id == null ? false : true,
                        available_classes: this.classes,
                        hn: parseFloat(element.HNormal).toFixed(2),
                        hext: parseFloat(element.HExtra).toFixed(2),
                        local: null,
                        faturar: null,
                        faturar_values: ['S', 'N', 'C'],
                        valor_h_normal: null,
                        valor_h_extra: null,
                        primavera_integration_error: element.primavera_integration_error,
                        options: ''
                    })                    
                });
            });

            this.fields.funcionariosInternos.forEach(element => {
                if (element.classe_id != null) {
                    if (!this.hideFrentes) {
                        this.frentes_obra.forEach(f => {
                            if (element.frente_id == f.ID) {
                                if (Array.isArray(f.classes) && f.classes.length > 0) {
                                    element.available_classes = f.classes
                                }
                            }
                        })
                    } else {
                        element.available_classes = this.classes
                    }
                    element.classeNeeded = true
                }
                returner.push(element)
            });

            return returner.slice(this.paginacaoInterna.menor, this.paginacaoInterna.maior)
        },

        canSave(){
            // Verificar se há 1 linha de mao de obra externa/interna
            let arrays_vazios = 0
            if(this.fields.funcionariosInternos.length == 0)
                arrays_vazios++
            if(this.fields.funcionariosExternos.length == 0)
                arrays_vazios++
            if(this.fields.equipamentosInternos.length == 0)
                arrays_vazios++
            if(this.fields.equipamentosExternos.length == 0)
                arrays_vazios++

            return arrays_vazios
        }, 
        getWindowHeight(){
            //Altura da janela menos 20%

            let calculo = window.innerHeight * 0.45
            
            return window.innerHeight - calculo
        },
    }
}
</script>
<style>

.my-grey{
    background-color: #ededed;
}

.my-orange {
    background-color: #FFE5CC;
}

.my-orange:hover {
    background-color: #FFE5CC;
}

.primavera-integration-error-row {
    background-color: #FFF8D6 !important;
}

.primavera-integration-error-row:hover {
    background-color: #FFF3BF !important;
}

</style>
<style scoped>
.rejected-alert {
  border-width: 1px !important;
}

.rejection-comment {
  white-space: pre-wrap;
  line-height: 1.45;
}

.premium-title {
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  font-size: 0.95rem;
  line-height: 1.2;
  display: inline-block;
}

</style>